// The reducer. `dispatch(state, action)` is the ONLY way state changes,
// locally or online - the house shape (thunderstone, camel-up, all of them).
// Pure, deterministic given the seed, and every event that matters is
// written to `log` so the table can narrate it.

import {
  PHASES, ROLES, ROLE_INFO, START, SEED_DEPTH, GOAL_DEPTH, MONUMENT_STAGES, LOSS,
  FOOD_PER_PERSON, FAMINE_HAPPINESS, FAMINE_POPULATION, shiftsFor,
  UNIT_KINDS, VILLAGE, TOWN, HAPPINESS_FADE_ABOVE, HAPPINESS_FADE_FAST_ABOVE, GLAD_WORKER_AT, BIRTH, RAIDER_KINDS, START_CAMP_AGE, RIVAL, GARRISON_BASE, CAMP, CAMP_RETURN, campStrengthIn,
  HALL_BUYS_PER_DAY, MOOT_WIN, RIVAL_WONDER_EVERY, WONDER_RESERVE, VICTORY, LOG_KEEP,
  YEAR_GOALS_OFFERED, TALES_OFFERED, KEEPSAKE_AFTER, HARVEST_TIMES, VETERAN_AT, VILLAGE_NAMES, VILLAGE_NAME_MAX, BAND, PARLEY_HOLDS_ON, THREAT_SHAPE, CITY,
} from './constants';
import {
  seedRivals, rivalById, rivalsOf, nextClaim, raidFrom, inSight, warmer, colder, rivalColour, rivalBlurb, wayOf, wayText, nearestUnmet,
} from './rivals';
import {
  key as hexKey, within, siteAt, TOWN_RADIUS, hexDistanceBetween, neighbours, isLandHex, NEAR_SITES,
} from './map';
import {
  EVENTS, BASE_POOL, GOAL_CARD, TECHS, REGIONS, REGION_ORDER, FAR_REGION_ORDER, DIE, sitesOf, AGES, ageOf, ALL_POOL_CARDS,
  GOALS, GOAL_ORDER, GOALS_OFFERED, goalOf, BRANCHES, wondersLeft, wondersOf,
  seasonPool, YEAR_GOALS, yearGoalsOpen, TALES, talesOpen, talesOf,
} from './cards';
import {
  yearOf, monthOf, dateOf, isYearCard, isHarvest, isMidwinter, isYearEnd, FREE_MONTHS, seasonOfMonth, seasonOf, MONTHS_PER_YEAR,
} from './calendar';
import {
  applyPatch, canAfford, costPatch, withTotals, describeCost, syncThreat, countsOf, changeBetween, describeGain,
  housingOf,
} from './effects';
import {
  findAction, resolveCost, resolveLabel, checkAction, workersDue, capacity, modsOf,
  fieldFood, workshopGoods, marketSale, placeName, lootOf,
  villagesOf, villageAt, villageYield, warbandCap, musterPoints, caravanLoad,
} from './roleActions';
import { ourHitOn, unitName } from './units';
import {
  unitsOf, unitById, raidersOf, describeUnit, seenFrom, exchange,
  sendsTonight, spawnHexes, raiderFor, raiderTarget, raiderStep, blockedBy, takenTownAt,
} from './units';
import {
  startCity, cityOf, BUILDINGS, foodCap, goodsCap, edgeHexToward, count as cityCount,
} from './city';
import {
  startHall, hallOf, HALL_CARDS, draw as drawCards, refillMarket, grieve, pushCompass, dealFor, COMPASS_MAX, governmentOf, GOVERNMENTS, seatFounders,
  COUNSELORS, seatFromDoor, dismiss as dismissCounselor, buysOf, dropCharter,
  retiring, retire, drawOffer, drawMoot, chooseCounselor, councilOf, dealMissingTerms, offerOf, COUNCIL_SEATS, remember, keepsakeOf, rotateShelf, dealBurden, emerging,
} from './hall';
import {
  cardEffect, cardGives, cardPush, cardDraws, sendsToBand, bonusesFor,
} from './roleActions';
import {
  startLore, loreOf, natureKey, mix as mixNatures, describeShared, satisfies, revealAspect, revealsBetween, PRINCIPLE_NAMES, describeNature, sameNature, PRINCIPLES, ASPECTS,
  dealMissingReactions, patchLore, knownOf,
} from './lore';
import {
  dealMissingMachines, machineById, inputNature, inputName, productName, fireNatures, lawOf, shelfAdd,
  fittingProduct, MACHINES,
} from './machines';
import {
  THEORY_HOLDS, THEORY_DEBUNKED,
} from './constants';
import { createRngState, shuffle, nextInt } from './rng/rng';
import { findAt, FINDS, workAt } from './terrain';
import { extendWorld, campSpots } from './frontier';
import { UPGRADES, surveyTarget } from './roleActions';
import {
  DEMANDS, demandsAwake, demandGap, offerDemands, demandMet, demandReward, demandPenalty, demandWhen,
} from './demands';
import { roadLevel, canLayRoad, roadBonus } from './roads';
import { VICTORIES, victoryWon, rivalVictor, nextProsperNights } from './victory';

export const ACTIONS = {
  ALLOCATE: 'ALLOCATE',
  START_DAY: 'START_DAY',
  ACT: 'ACT',
  UNDO: 'UNDO',
  DONE: 'DONE',
  UNDONE: 'UNDONE',
  RESOLVE_EVENT: 'RESOLVE_EVENT',
  CONTINUE: 'CONTINUE',
  // The table decides together (Matt, playtest 9): everybody says flip,
  // everybody votes. See applyVoteFlip / applyVote.
  VOTE_FLIP: 'VOTE_FLIP',
  VOTE: 'VOTE',
  // Anyone seated may rename a village or town (playtest 9).
  RENAME: 'RENAME',
  // A role asks the table for something (playtest 9: "hey Matt, can you get
  // me an extra worker this turn?"). Shown on the TV and at dawn.
  NEED: 'NEED',
  // THE NIGHT IS READ CARD BY CARD (Matt, after playtest 12: "a series of
  // cards... that show up and we have to dismiss them... any one person
  // tapping next can move it along"). One tap moves every screen on.
  NIGHT_NEXT: 'NIGHT_NEXT',
};
// What a role may ask the table for.
export const NEEDS = ['workers', 'food', 'goods', 'coin', 'knowledge'];

// Dawn is a SHARED decision: the hands are divided in the state so every
// phone (and the table) sees the same split. ALLOCATE nudges one role's
// share; START_DAY locks it in and the day begins for everyone at once.
export const allocateAction = (role, delta) => ({ type: ACTIONS.ALLOCATE, role, delta });
export const startDayAction = (by = null, force = false) => ({ type: ACTIONS.START_DAY, by, force });
export const doneAction = (role) => ({ type: ACTIONS.DONE, role });
export const undoAction = (role) => ({ type: ACTIONS.UNDO, role });
// Playtest 6: "once you do it, you can't go back." Now you can, while it is still day.
export const undoneAction = (role) => ({ type: ACTIONS.UNDONE, role });
export const actAction = (role, actionId, params = {}) => ({
  type: ACTIONS.ACT, role, actionId, params,
});
export const resolveEventAction = (choice) => ({ type: ACTIONS.RESOLVE_EVENT, choice });
export const continueAction = () => ({ type: ACTIONS.CONTINUE });
export const nightNextAction = (round) => ({ type: ACTIONS.NIGHT_NEXT, round });
// `by` is the player; `force` decides with whoever has said so far (one
// person who wandered off must not hold the table hostage).
export const needAction = (role, need) => ({ type: ACTIONS.NEED, role, need });
export const renameAction = (by, village, name) => ({
  type: ACTIONS.RENAME, by, village, name,
});
export const voteFlipAction = (by, force = false) => ({ type: ACTIONS.VOTE_FLIP, by, force });
export const voteAction = (by, choice, force = false) => ({
  type: ACTIONS.VOTE, by, choice, force,
});
// The phase between December's night and January's dawn: the chronicle is
// on the table and the sitting is over. CONTINUE deals the new year.
export const YEAR_END = 'yearend';

// ------------------------------------------------------------ creation

export const createGameState = ({ players, roles, seed }) => {
  let rng = createRngState(seed);
  // Deal the first year's deck: one card for each free month from that
  // month's season (DESIGN.md "The Year"). The first Vision goes somewhere
  // in the top band, so the year has a shape: a few months of finding your
  // feet, then a purpose.
  let deck;
  [deck, rng] = dealYearCards([], rng);
  let goalAt;
  [goalAt, rng] = nextInt(GOAL_DEPTH.max - GOAL_DEPTH.min + 1, rng);
  deck.splice(GOAL_DEPTH.min + goalAt, 0, GOAL_CARD);
  let regionDeck;
  [regionDeck, rng] = shuffle(REGION_ORDER, rng);
  // The far ring gets the far regions, so the near six (and their stories)
  // are always the first year's.
  // The far regions wait in a deck of their own: the frontier deals one to
  // each far site as it is found (engine/frontier.js).
  let farDeck;
  [farDeck, rng] = shuffle(FAR_REGION_ORDER, rng);
  // The two goals the vision will offer this era.
  let goalOffer;
  // The first Vision offers only the first era's wonders; the later ones
  // join the offers once a wonder stands (and old seeds keep their draw).
  [goalOffer, rng] = shuffle(GOAL_ORDER.filter((id) => !GOALS[id].era), rng);
  goalOffer = goalOffer.slice(0, GOALS_OFFERED);
  // The Hall's deck, shuffled; its first hand and market are dealt below.
  let hall;
  [hall, rng] = startHall(rng);
  // The Scholars' world: eight natures dealt, a reaction for every tech.
  let lore;
  [lore, rng] = startLore(rng, TECHS);
  // The other peoples on the island, seated far out.
  let rivals;
  [rivals, rng] = seedRivals({ villages: [], menaces: [], rivals: [], mapSeed: seed }, rng);

  const state = {
    seed,
    rng,
    meta: {
      seq: 0, phase: PHASES.DAWN, round: 1, era: 1, version: 2,
    },
    players: players.map((p, i) => ({ id: p.id, name: p.name, seat: i })),
    // role -> player id. A player may hold several roles (solo play holds
    // all four). Roles are the seats; players are the people in them.
    roles: ROLES.reduce((acc, role) => ({ ...acc, [role]: roles?.[role] ?? players[0].id }), {}),
    // The round's hands: the split being argued at dawn, what each role has
    // left during the day, who has finished, and what each has done today.
    allocation: emptyByRole(0),
    hands: emptyByRole(0),
    done: emptyByRole(false),
    taken: emptyByRole(null),
    // Extra hands owed to a role at the next dawn (a held theory teaches someone).
    boons: emptyByRole(0),
    civ: {
      ...START,
      // The first round's workers are already in the square.
      workers: workersDue(START),
      techs: [],
      regions: [],
      // One region per direction, dealt face down. Explore picks a
      // direction and finds whatever was dealt there.
      regionSites: Object.fromEntries(regionDeck.map((id, i) => [i, id])),
      goal: null,
      goalOffer,
      // Branches of the tech tree other roles have opened for Science.
      branches: [],
      monument: { revealed: false, stages: 0 },
      // The Frontier: pieces on the map. One warband at the city to begin
      // with; the Stewards train the rest. See engine/units.js.
      units: [{ id: 'u1', kind: 'warband', q: 0, r: 0, strength: UNIT_KINDS.warband.startStrength }],
      unitSeq: 1,
      // What the camps have sent out and where it has got to.
      raiders: [],
      raiderSeq: 0,
      // What the Frontier has planted. A village sends up what the ground
      // makes; the Hall charters one into a town.
      villages: [],
      villageSeq: 0,
      // The Stewards: three hexes, two fields, a market, a workshop.
      city: startCity(),
      // The Hall: a deck of customs, a hand, a market, the compass.
      hall,
      // The Scholars: hidden natures, reactions, experiments, theories.
      lore,
      seen: within(0, 0, TOWN_RADIUS + 1).map(([q, r]) => hexKey(q, r)),
      menaces: [],
      menaceSeq: 0,
      // Things worth walking to: ruins, a grove, a wreck, a lode. Scattered
      // once from the seed, hidden under the fog until somebody looks.
      // Found by the frontier as the band moves out (engine/frontier.js).
      finds: [],
      findsTaken: [],
      farSites: [],
      farDeck,
      mapSeed: seed,
      // The Founding (cards.js AGES).
      age: 0,
      threat: 0,
      // The other peoples, and the wonders raised so far.
      rivals,
      wonders: [],
      // The year: the winter tales this campaign keeps, the year card's
      // goal, and what the town looked like when the year began.
      tales: [],
      yearGoal: null,
      // The people's demands (engine/demands.js): none pending, and none
      // offered before the first spring is out.
      demand: null,
      demandNext: 4,
      yearStart: null,
    },
    deck,
    discard: [],
    removed: [],
    // One entry per year that has ended: the campaign's book.
    chronicle: [],
    regionDeck,
    event: null,
    result: null,
    log: [],
  };
  // The first band of the world: finds within reach of the first walks.
  let civ = extendWorld(syncCity(state.civ));
  let rng2 = state.rng;
  // No hand yet: the Hall's hand is dealt when the first day starts, two
  // cards for every worker the dawn split gives it (applyStartDay).
  let dealt;
  [dealt, rng2] = refillMarket(civ.hall, civ, rng2);
  civ = { ...civ, hall: dealt };
  // ONE CAMP FROM THE START, out in the fog. Camps only ever came from
  // cards, and a season with no Raiders card in it (one in three) gave the
  // Frontier nothing to find for ten rounds: "threat literally never went
  // above zero" (playtest 6). Now there is always something out there -
  // on a FUSE: it starts its count below zero, so it sends nothing for the
  // first nights and is only busy by round ten or so. The bots, who never
  // walk to a camp, lost 73% of games to one that was busy from night one;
  // a Frontier who walks the four hexes clears it before it wakes.
  let started = { ...state, rng: rng2, civ };
  [started] = spawnMenace(started, { kind: 'camp', strength: RAIDER_KINDS.camp.strength, age: START_CAMP_AGE });
  // The founding counselors' terms and the machines' laws, last of all (see
  // startHall and machines.js).
  let dealtCiv;
  let rng3 = started.rng;
  [dealtCiv, rng3] = dealMissingTerms(started.civ, rng3);
  [dealtCiv, rng3] = dealMissingMachines(dealtCiv, rng3);
  [dealtCiv, rng3] = dealMissingReactions(dealtCiv, TECHS, rng3);
  let firstSplit;
  [firstSplit, rng3] = defaultSplit(dealtCiv.workers, null, rng3);
  // The burden slot (playtest 13), dealt after everything the seed decided before it existed.
  let hallB;
  [hallB, rng3] = dealBurden(dealtCiv.hall, rng3);
  dealtCiv = { ...dealtCiv, hall: hallB };
  started = { ...started, civ: { ...dealtCiv, yearStart: yearSnapshot(dealtCiv) }, rng: rng3, allocation: firstSplit };
  return logLine(started, { kind: 'order', text: 'Raiders have made camp somewhere beyond the fog.' });
};

// ---------------------------------------------------------------- helpers

const emptyByRole = (v) => ROLES.reduce((acc, r) => ({ ...acc, [r]: v === null ? {} : v }), {});

/**
 * The civ as a role sees it during the day: `workers` is THAT role's hands,
 * not the idle pool. Every cost check and every board reads through this.
 */
export const civFor = (state, role) => {
  if (!state || state.meta?.phase !== PHASES.DAY || !state.hands) return state?.civ;
  return { ...state.civ, workers: state.hands[role] ?? 0 };
};

const bump = (state) => ({ ...state, meta: { ...state.meta, seq: state.meta.seq + 1 } });

const logLine = (state, entry) => {
  const log = [...state.log, { seq: state.meta.seq, round: state.meta.round, ...entry }];
  if (log.length <= LOG_KEEP) return { ...state, log };
  const drop = log.length - LOG_KEEP;
  return { ...state, log: log.slice(drop), logBase: (state.logBase || 0) + drop };
};
/** Entries ever written, including those trimmed away: a count that only grows. */
export const logTotal = (state) => (state?.logBase || 0) + (state?.log?.length || 0);
/** The entry at an absolute position, or undefined if it has been trimmed. */
export const logAt = (state, abs) => state?.log?.[abs - (state?.logBase || 0)];

/**
 * Shuffles a card into the top SEED_DEPTH of the deck. Sight unseen.
 *
 * Never above the goal card, though: playtest 2 buried the monument until
 * round 14 because every action pushed it one deeper. While the goal is
 * still in the deck, echoes queue up underneath it - the era settles in,
 * the vision arrives on schedule, then the consequences start landing.
 */
export const seedCard = (state, cardId) => {
  const deck = state.deck.slice();
  const goalAt = deck.indexOf(GOAL_CARD);
  const floor = goalAt >= 0 ? goalAt + 1 : 0;
  const band = Math.min(SEED_DEPTH, deck.length - floor);
  let at;
  let { rng } = state;
  [at, rng] = nextInt(band + 1, rng);
  deck.splice(floor + at, 0, cardId);
  return { ...state, deck, rng };
};

/**
 * Puts a card where it turns NEXT month: on top, or just under the first
 * Vision while that is still waiting, so the goal is never buried. For news
 * that cannot wait - a people at the gate (playtest 9: the Painted Folk's
 * envoys went in in February and were still in the deck in December).
 */
export const seedNext = (state, cardId) => {
  const deck = state.deck.slice();
  const goalAt = deck.indexOf(GOAL_CARD);
  deck.splice(goalAt >= 0 ? goalAt + 1 : 0, 0, cardId);
  return { ...state, deck };
};

// Gone for good: out of today's deck AND off every later season's deal
// (seasonPool reads `removed`). It used to note only the copies in the deck
// that day, so a Drought "removed" in spring came back next year.
export const removeCards = (state, ids) => ({
  ...state,
  deck: state.deck.filter((id) => !ids.includes(id)),
  removed: [...new Set([...state.removed, ...ids])],
});


/**
 * A technology that opens a Hall card puts that card STRAIGHT INTO THE
 * HALL'S DECK, free (playtest 14, Elizabeth: "if Carrie is making
 * discoveries, that should create new cards coming into my pile"). The
 * shelf can still offer another copy. Pushes a line.
 */
const dealOpened = (civ, techId, lines) => {
  const opened = Object.entries(HALL_CARDS).filter(([, c]) => c.needsTech === techId && c.price).map(([id]) => id);
  if (!opened.length || !hallOf(civ)) return civ;
  let out = civ;
  opened.forEach((id) => { out = grieve(out, id); });
  lines.push(`the Hall's deck gains ${opened.map((id) => HALL_CARDS[id].name).join(' and ')}`);
  return out;
};

/**
 * A technology is learned: by a eureka on the bench or made from the shelf,
 * the same path. Returns [civ, state] (the state, because a technology may
 * take cards out of the deck); pushes its lines.
 */
const learnTech = (civ, state, id, lines) => {
  let next = state;
  civ = { ...civ, techs: [...civ.techs, id] };
  civ = dealOpened(civ, id, lines);
  if (TECHS[id].removes) {
    const before = next.deck.length;
    next = removeCards(next, TECHS[id].removes);
    const n = before - next.deck.length;
    if (n) lines.push(`${n} ${n === 1 ? 'card' : 'cards'} removed from the deck`);
  }
  // The idea it gives birth to comes tonight. (It was a Eureka card in the
  // deck, and seven of them in a good year pushed the year's troubles past
  // December, where they were let go: after playtest 9, Matt, "the game felt
  // too easy".)
  civ = { ...civ, ideasDue: (civ.ideasDue || 0) + 1, ideasFrom: [...(civ.ideasFrom || []), id] };
  lines.push('another idea will come of it tonight');
  return [civ, next];
};

const finish = (state, result) => ({
  ...state,
  meta: { ...state.meta, phase: PHASES.FINISHED },
  result,
});

const describePush = ({ x = 0, y = 0 }) => [
  x ? `${Math.abs(x)} toward ${x < 0 ? 'Tradition' : 'Novelty'}` : '',
  y ? `${Math.abs(y)} toward ${y < 0 ? 'Order' : 'Liberty'}` : '',
].filter(Boolean).join(' and ');

/** civ.markets / civ.farms mirror the city for everything that still reads them. */
const syncCity = (civ) => ({
  ...civ, markets: cityCount(civ, 'market'), farms: cityCount(civ, 'field'), libraries: cityCount(civ, 'library'),
});

// ---------------------------------------------------------------- the map

// ENCOUNTERS. Matt, playtest 6: "the frontier, as they encounter things,
// needs to have more of a stopping message... a message that stops them
// from doing other things, they have to make a choice or at least dismiss
// it." So an act log entry (and the night's) carries `notices`: the things
// that changed on the map which the Frontier's phone should put in front
// of the player until dismissed. Worked out by comparing the civ before
// and after, so nothing has to remember to say it.
const MENACE_TITLES = { camp: 'A raider camp', den: 'A bandits\' den', wolves: 'A wolves\' den' };
// WHAT THE SCHOLARS LEARNED without doing anything (playtest 13: "number is
// light" and "water is wet" sat in Andrew's notebook with no idea when they
// came). Every log entry that could carry one does: `reveals` is the list
// of natures the world read out, and `from` is what did it. The Scholars'
// board stops on them like the Frontier stops on its notices.
const revealsEntry = (before, after, from) => {
  const list = revealsBetween(before, after);
  return list.length ? { reveals: list.map((r) => ({ ...r, from })) } : {};
};
const frontierNotices = (before, after) => {
  const out = [];
  const seenBefore = new Set(before.seen || []);
  const seenAfter = new Set(after.seen || []);
  (after.regions || []).filter((id) => !(before.regions || []).includes(id)).forEach((id) => {
    const region = REGIONS[id];
    if (region) out.push({ kind: 'region', title: `${region.name} rises`, text: region.text || 'New ground, and whatever was dealt to it.' });
  });
  (before.finds || []).filter((f) => !(after.finds || []).some((g) => g.id === f.id)).forEach((f) => {
    const what = FINDS[f.kind];
    if (what) out.push({ kind: 'find', title: what.name, text: `${what.text} ${describeGain(what.gain)}.` });
  });
  (after.menaces || []).forEach((m) => {
    const k = hexKey(m.q, m.r);
    if (seenAfter.has(k) && !seenBefore.has(k)) {
      out.push({ kind: 'camp', title: `${MENACE_TITLES[m.kind] || 'A camp'}, strength ${m.strength}`, text: `At ${placeName(after, m.q, m.r)}. The longer it stands, the more raiders it sends. Fight it from the next hex, or parley.` });
    }
  });
  rivalsOf(after).forEach((r) => {
    const was = rivalsOf(before).find((x) => x.id === r.id);
    if (!inSight(after, r) || (was && inSight(before, was))) return;
    out.push({
      kind: 'rival', title: `Another people: ${r.name}`, colour: rivalColour(r),
      text: `${rivalBlurb(r)} ${wayText(r)} They are ${r.stance}, strength ${r.power}. Their ground is marked in their colour and can't be walked on. From beside it: raid or parley; the Hall can send an Envoy.`,
    });
  });
  raidersOf(after).filter((x) => !raidersOf(before).some((y) => y.id === x.id) && seenAfter.has(hexKey(x.q, x.r))).forEach((x) => {
    out.push({ kind: 'raiders', title: `${x.name} are abroad, strength ${x.strength}`, text: `At ${placeName(after, x.q, x.r)}. Heading for your nearest village or the city. A unit in their way stops them.` });
  });
  (after.villages || []).filter((v) => v.damaged && !(before.villages || []).find((w) => w.id === v.id)?.damaged).forEach((v) => {
    out.push({ kind: 'loss', title: `${v.name} is wrecked`, text: 'It sends nothing until a unit stands on it and repairs it.' });
  });
  (before.villages || []).filter((v) => !(after.villages || []).some((w) => w.id === v.id)).forEach((v) => {
    out.push({ kind: 'loss', title: `${v.name} is burned to the ground`, text: 'Gone, charter and all. A pioneer can plant a new one.' });
  });
  unitsOf(before).filter((u) => !unitsOf(after).some((v) => v.id === u.id) && u.kind !== 'pioneer').forEach((u) => {
    out.push({ kind: 'loss', title: `${describeUnit(u)} is lost`, text: 'The Stewards can train another.' });
  });
  (before.menaces || []).filter((m) => !(after.menaces || []).some((n) => n.id === m.id)).forEach((m) => {
    out.push({ kind: 'cleared', title: `${MENACE_TITLES[m.kind] || 'The camp'} is gone`, text: 'Nothing will come out of it again. What it held is yours.' });
  });
  return out;
};

/** The commonwealth sees `radius` around a hex. */
const reveal = (civ, q, r, radius) => {
  const seen = new Set(civ.seen || []);
  within(q, r, radius).forEach(([a, b]) => seen.add(hexKey(a, b)));
  // The world reaches out as far as the eye does (engine/frontier.js).
  return extendWorld({ ...civ, seen: [...seen] });
};

/** Something appears on the map, at a free spawn site - seen ones first. Returns [state, line]. */
/**
 * ONE EXCHANGE of an attrition fight. Both sides roll their strength, each
 * hit takes one strength off the other, simultaneously. Nobody is wiped out
 * by arithmetic: a camp too big for one warband is what a second warband is
 * for. Returns [civ, lines, rolls, rng].
 */
const resolveFight = (civ0, { unit, foe }, rng0) => {
  let civ = civ0;
  const u = unitById(civ, unit);
  const camp = (civ.menaces || []).find((m) => m.id === foe);
  const raider = raidersOf(civ).find((x) => x.id === foe);
  const target = camp || raider;
  if (!u || !target) return [civ, [], null, rng0];

  let out;
  let rng;
  [out, rng] = exchange(civ, u, target, rng0);
  const what = camp ? camp.kind : (raider.name || 'raiders').toLowerCase();
  const lines = [`${describeUnit(u, civ)} rolled ${out.mine.join(' + ')}: ${out.ourHits} ${out.ourHits === 1 ? 'hit' : 'hits'}`,
    `the ${what} rolled ${out.theirs.join(' + ')}: ${out.theirHits} back`];

  // Ours first, so a unit reduced to nothing is gone before anything else.
  if (out.ours <= 0) {
    civ = { ...civ, units: unitsOf(civ).filter((x) => x.id !== u.id) };
    lines.push(`${describeUnit(u)} is destroyed`);
  } else if (out.ours < u.strength) {
    civ = { ...civ, units: unitsOf(civ).map((x) => (x.id === u.id ? { ...x, strength: out.ours } : x)) };
    lines.push(`down to ${out.ours}`);
  }

  if (out.foe <= 0) {
    // A fight won and survived is a win; enough wins make a veteran.
    if (out.ours > 0) {
      const wins = (u.wins || 0) + 1;
      civ = { ...civ, units: unitsOf(civ).map((x) => (x.id === u.id ? { ...x, wins } : x)) };
      if (wins === VETERAN_AT) lines.push(`${unitName(u)} are veterans now: they hit one better`);
    }
    if (camp) {
      let got;
      [civ, got] = applyPatch({ ...civ, menaces: (civ.menaces || []).filter((m) => m.id !== camp.id), cleared: (civ.cleared || 0) + 1 }, lootOf(camp));
      lines.push(`the ${camp.kind} is cleared`, ...got);
    } else {
      civ = { ...civ, raiders: raidersOf(civ).filter((x) => x.id !== raider.id) };
      lines.push('the raiders are put down');
    }
  } else if (camp) {
    civ = { ...civ, menaces: (civ.menaces || []).map((m) => (m.id === camp.id ? { ...m, strength: out.foe } : m)) };
    lines.push(`the ${camp.kind} holds at ${out.foe}`);
  } else {
    civ = { ...civ, raiders: raidersOf(civ).map((x) => (x.id === raider.id ? { ...x, strength: out.foe } : x)) };
    lines.push(`they hold at ${out.foe}`);
  }
  return [syncThreat(civ), lines, out.mine, rng, out];
};

const spawnMenace = (state, { kind, strength, age = 0 }) => {
  const civ = state.civ;
  const taken = new Set([
    ...(civ.menaces || []).map((m) => hexKey(m.q, m.r)),
    ...villagesOf(civ).map((v) => hexKey(v.q, v.r)),
    ...unitsOf(civ).map((u) => hexKey(u.q, u.r)),
  ]);
  // Nothing camps next to a settlement you hold. That is what planting one
  // buys you besides its trickle: ground that stays quiet.
  const guarded = new Set(villagesOf(civ).flatMap((v) => within(v.q, v.r, 1).map(([a, b]) => hexKey(a, b))));
  // Only at the edge of the known (engine/frontier.js campSpots): out in the
  // fog band first, nearest the town, never on ground nobody will see for
  // years (Matt, after playtest 12).
  const free = campSpots(civ).filter(([q, r]) => !taken.has(hexKey(q, r)) && !guarded.has(hexKey(q, r)));
  if (!free.length) return [state, `${kind === 'camp' ? 'raiders' : kind} came, found no ground, and moved on`];
  const seenSet = new Set(civ.seen || []);
  const pick = free.slice(0, 12);
  let at;
  let { rng } = state;
  [at, rng] = nextInt(pick.length, rng);
  const [q, r] = pick[at];
  const id = `m${(civ.menaceSeq || 0) + 1}`;
  const menaces = [...(civ.menaces || []), { id, kind, q, r, strength, age }];
  const next = { ...state, rng, civ: syncThreat({ ...civ, menaces, menaceSeq: (civ.menaceSeq || 0) + 1 }) };
  const where = seenSet.has(hexKey(q, r)) ? 'in sight of the town' : 'somewhere out in the fog';
  return [next, `a ${kind} of strength ${strength} appears ${where}`];
};

/** A branch of the tree opens (once). Returns [state, line | null]. */
const openBranch = (state, branch, why) => {
  if (!BRANCHES[branch] || (state.civ.branches || []).includes(branch)) return [state, null];
  const civ = { ...state.civ, branches: [...(state.civ.branches || []), branch] };
  return [{ ...state, civ }, `a new branch of knowledge opens for Science: ${BRANCHES[branch].name}${why ? ` (${why})` : ''}`];
};

/**
 * A wonder's last stage stands: it GRANTS for the rest of the game, the
 * next Vision is dealt, and the game goes on. Nothing ends. Returns
 * [state, lines].
 */
const completeWonder = (state) => {
  const id = goalOf(state.civ);
  const goal = GOALS[id];
  if (!goal || !goal.build || !goal.done(state.civ)) return [state, []];
  let civ = {
    ...state.civ,
    wonders: [...wondersOf(state.civ), id],
    goal: null,
    monument: { revealed: false, stages: 0 },
    stoneLaid: false,
  };
  const lines = [`${goal.ending[0]}: ${goal.ending[1]}`];
  if (goal.build.onBuilt?.reveal) {
    for (let i = 0; i < goal.build.onBuilt.reveal; i += 1) {
      const unknown = PRINCIPLES.flatMap((p) => ASPECTS.filter((a) => loreOf(civ)?.known?.[p]?.[a] === undefined).map((a) => [p, a]));
      if (!unknown.length) break;
      let said;
      [civ, said] = revealAspect(civ, unknown[0][0], unknown[0][1]);
      if (said) lines.push(`the archive says: ${said}`);
    }
  }
  const grants = Object.entries(goal.grants || {});
  if (grants.length) lines.push(`from now on: ${goal.text.split('Finished, ')[1] || goal.text}`);
  let next = { ...state, civ };
  next = logLine(next, { kind: 'wonder', wonder: id, text: `${goal.name} stands. ${goal.ending[1]}` });
  // The next vision, if there is anything left to offer.
  const left = wondersLeft(civ);
  if (left.length) {
    let offer;
    let { rng } = next;
    [offer, rng] = shuffle(left, rng);
    next = { ...next, rng, civ: { ...next.civ, goalOffer: offer.slice(0, GOALS_OFFERED) } };
    next = seedCard(next, 'next_vision');
    lines.push('the next Vision goes into the deck');
  }
  return [next, lines];
};
/** Kept for the callers: a wonder finishing is not an ending any more. */
const checkGoal = (state) => state;

/** Loss checks, in the order they would be felt. Returns state (maybe finished). */
const checkCollapse = (state) => {
  if (state.meta.phase === PHASES.FINISHED) return state;
  const { civ } = state;
  if (civ.population <= 0) return finish(logLine(state, { kind: 'end', text: 'The last of the people are gone. The game is lost.' }), { won: false, reason: LOSS.COLLAPSE });
  if (civ.happiness <= 0) return finish(logLine(state, { kind: 'end', text: 'Happiness is 0: the people revolt. The game is lost.' }), { won: false, reason: LOSS.REVOLT });
  // (The threat METER no longer ends the game, after playtest 12: the bots
  // lost with every building standing, raiders held at the walls by nine
  // warbands. A town is lost to what raiders DO - the city sacked with
  // nothing left to burn, in applyContinue. Threat is the warning.)
  const won = victoryWon(civ);
  if (won) return finish(logLine(state, { kind: 'end', text: `Victory: ${VICTORIES[won].name}. ${VICTORIES[won].text}` }), { won: true, reason: won });
  const theirs = rivalVictor(civ);
  if (theirs) return finish(logLine(state, { kind: 'end', text: `${theirs.name} have raised their wonders first: they win Legacy.` }), { won: false, reason: LOSS.OUTPACED, by: theirs.name });
  return state;
};

// ------------------------------------------------------------ the dawn split
//
// THE SPLIT STARTS EVEN (Matt, 2026-09-28: "the dawn split defaults to an
// even split across the four roles - as even as it can get, with the extras
// applied randomly at first and then based on the previous round after
// that. Then the players can decide if they want to rearrange things").
// Pure: the workers, last dawn's split, the rng. Returns [allocation, rng].
export const defaultSplit = (workers, previous, rng) => {
  const base = Math.floor(workers / ROLES.length);
  let extra = workers - base * ROLES.length;
  const split = emptyByRole(base);
  const hadPrevious = previous && ROLES.some((r) => (previous[r] || 0) > 0);
  let order;
  if (hadPrevious) order = ROLES.slice().sort((a, b) => (previous[b] || 0) - (previous[a] || 0) || ROLES.indexOf(a) - ROLES.indexOf(b));
  else [order, rng] = shuffle(ROLES.slice(), rng);
  for (const r of order) { if (extra <= 0) break; split[r] += 1; extra -= 1; }
  return [split, rng];
};

// ------------------------------------------------------------ the year
//
// THE SEASONS LIVE IN THE DECK (Matt, 2026-09-27, after twice rejecting
// seasons as rule changes: "we've made winter sound like it's going to be
// really unfun"). Each January the deck is dealt for the year: one card
// for each free month, drawn from that month's season's pool, in month
// order. Nobody's turn changes; the world's card does. Stories the town
// seeded last year and has not turned yet stay on top; leftover weather is
// let go, so the deck cannot grow year on year.

/** The nine season cards for a year, in month order. Returns [ids, rng]. */
const dealYearCards = (removed, rng, age = 0) => {
  const used = new Set();
  const out = [];
  for (const month of FREE_MONTHS) {
    const season = seasonOfMonth(month);
    let pool = seasonPool(season, removed, age).filter((id) => !used.has(id));
    if (!pool.length) pool = BASE_POOL.filter((id) => !removed.includes(id) && !used.has(id));
    if (!pool.length) break;
    let at;
    [at, rng] = nextInt(pool.length, rng);
    used.add(pool[at]);
    out.push(pool[at]);
  }
  return [out, rng];
};

/** January: the new year's deck under whatever stories are still waiting. */
const dealYear = (state) => {
  const stories = state.deck.filter((id) => !BASE_POOL.includes(id) && !ALL_POOL_CARDS.includes(id));
  let cards;
  let { rng } = state;
  [cards, rng] = dealYearCards(state.removed, rng, state.civ.age || 0);
  return { ...state, deck: [...stories, ...cards], rng };
};

/** What the chronicle measures a year against. */
const yearSnapshot = (civ) => ({
  population: civ.population, happiness: civ.happiness, techs: (civ.techs || []).length, wonders: (civ.wonders || []).length,
  villages: (civ.villages || []).length, towns: (civ.villages || []).filter((v) => v.chartered).length,
  regions: (civ.regions || []).length, roads: (civ.roads || []).length, laws: (civ.hall?.laws || []).length,
  cleared: civ.cleared || 0, met: (civ.rivals || []).filter((r) => r.met).length,
});

/**
 * The year's page in the book: what changed, in plain lines, from the
 * snapshot taken in January and the log since. Written at December's
 * night; shown on the table until somebody taps Done, and again as the
 * recap when the next sitting opens.
 */
const writeChronicle = (state, civ, endedRound) => {
  const year = yearOf(endedRound);
  const was = civ.yearStart || yearSnapshot(civ);
  const now = yearSnapshot(civ);
  const lines = [];
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const since = (state.log || []).filter((l) => l.round >= (year - 1) * MONTHS_PER_YEAR + 1);
  const wonders = since.filter((l) => l.kind === 'wonder').map((l) => l.text.replace(/\.$/, ''));
  if (wonders.length) lines.push(...wonders);
  if (now.techs > was.techs) lines.push(`the Scholars learned ${plural(now.techs - was.techs, 'technology', 'technologies')}${(civ.techs || []).slice(was.techs).length ? `: ${(civ.techs || []).slice(was.techs).map((id) => TECHS[id]?.name || id).join(', ')}` : ''}`);
  if (now.regions > was.regions) lines.push(`the Frontier found ${plural(now.regions - was.regions, 'new region', 'new regions')}: ${(civ.regions || []).slice(was.regions).map((id) => REGIONS[id]?.name || id).join(', ')}`);
  if (now.villages > was.villages) lines.push(`${plural(now.villages - was.villages, 'village was', 'villages were')} founded`);
  if (now.villages < was.villages) lines.push(`${plural(was.villages - now.villages, 'village was', 'villages were')} lost`);
  if (now.towns > was.towns) lines.push(`${plural(now.towns - was.towns, 'town was', 'towns were')} chartered`);
  if (now.roads > was.roads) lines.push(`${plural(now.roads - was.roads, 'hex', 'hexes')} of road laid`);
  if (now.cleared > was.cleared) lines.push(`${plural(now.cleared - was.cleared, 'camp', 'camps')} cleared`);
  if (now.laws > was.laws) lines.push(`${plural(now.laws - was.laws, 'law', 'laws')} written down`);
  const seated = since.filter((l) => /takes the chair|takes a chair|joins the council|is seated/i.test(l.text || '')).length;
  const retired = since.map((l) => (/(The [A-Z][a-z]+) retires/.exec(l.text || '') || [])[1]).filter(Boolean);
  if (retired.length) lines.push(`${retired.join(', ')} retired from the Hall${retired.some((n) => keepsakeOf(n.replace('The ', '').toLowerCase())) ? '' : ''}`);
  if (seated) lines.push(`${plural(seated, 'new counselor', 'new counselors')} took a chair`);
  if (now.population > was.population) lines.push(`the town grew by ${plural(now.population - was.population, 'person', 'people')} (${now.population} now)`);
  if (now.population < was.population) lines.push(`${plural(was.population - now.population, 'person', 'people')} lost (${now.population} now)`);
  const famines = since.filter((l) => l.kind === 'upkeep' && /famine/.test(l.text)).length;
  if (famines) lines.push(`${plural(famines, 'famine', 'famines')}`);
  const lost = since.filter((l) => l.kind === 'upkeep' && /finish .* - it is theirs now/.test(l.text)).map((l) => (/([A-Z][^,]*?) finish (The [^-]+?) - it is theirs/.exec(l.text) || []).slice(1, 3)).filter((x) => x.length);
  lost.forEach(([who, what]) => lines.push(`${who} finished ${what.trim()}`));
  if (now.met > was.met) lines.push(`${plural(now.met - was.met, 'people was', 'peoples were')} met: ${(civ.rivals || []).filter((r) => r.met).slice(was.met).map((r) => r.name).join(', ')}`);
  if (civ.yearGoal) lines.push(civ.yearGoal.met ? `the year's goal was met: ${YEAR_GOALS[civ.yearGoal.id]?.name}` : `the year's goal was missed: ${YEAR_GOALS[civ.yearGoal.id]?.name}`);
  const tale = talesOf(civ).at(-1);
  if (tale && civ.taleYear === year) lines.push(`the tale the town keeps: ${TALES[tale].name}`);
  if (!lines.length) lines.push('a quiet year');
  return { year, lines, ended: endedRound, population: now.population, happiness: now.happiness, wonders: now.wonders, techs: now.techs, tales: talesOf(civ).length };
};

// ------------------------------------------------------------ the round

const applyAllocate = (state, { role, delta }) => {
  if (state.meta.phase !== PHASES.DAWN || !ROLES.includes(role) || !Number.isInteger(delta)) return state;
  const allocation = { ...(state.allocation || emptyByRole(0)) };
  const total = ROLES.reduce((n, r) => n + (allocation[r] || 0), 0);
  const next = (allocation[role] || 0) + delta;
  if (next < 0 || total + delta > state.civ.workers) return state;
  allocation[role] = next;
  // A changed split asks everyone again.
  return bump({ ...state, allocation, startVotes: null });
};

/**
 * Dawn ends: the split is locked and everybody's day begins at once. With
 * more than one person seated, everyone taps Start (Matt, playtest 10: "a
 * nice little moment of unification"); `force` starts with the taps so far.
 * Bots never tap. A tap with no `by` (solo, tests) starts at once.
 */
const applyStartDay = (state, action = {}) => {
  if (state.meta.phase !== PHASES.DAWN) return state;
  const seated = seatedIds(state);
  if (action.by && seated.length > 1 && !action.force) {
    if (!seated.includes(action.by)) return state;
    const startVotes = { ...(state.startVotes || {}), [action.by]: true };
    if (!seated.every((id) => startVotes[id])) return bump({ ...state, startVotes });
    state = { ...state, startVotes: null };
  } else if (state.startVotes) {
    state = { ...state, startVotes: null };
  }
  // A game dealt before the council became the board sat down with one
  // counselor. Fill the empty seats it is owed, so a save in progress does
  // not have to be abandoned.
  state = { ...state, civ: seatFounders(state.civ, state.meta.round) };
  const allocation = { ...emptyByRole(0), ...(state.allocation || {}) };
  const given = ROLES.reduce((n, r) => n + allocation[r], 0);
  const idle = Math.max(0, state.civ.workers - given);
  const boons = { ...emptyByRole(0), ...(state.boons || {}) };
  // The Stewards place half-days, so their people arrive as two tokens each.
  const hands = Object.fromEntries(ROLES.map((r) => [r, shiftsFor(r, allocation[r] + (boons[r] || 0))]));
  // THE HALL'S WORKERS BECOME ITS HAND (Matt, 2026-10-14). Every worker the
  // split gave the Hall is two cards, dealt now, and the workers are spent
  // on the dealing: the Hall's day is its hand and its three seats. (A
  // Tradition that draws one fewer takes it off the deal.)
  let civ = { ...state.civ, workers: idle };
  let { rng } = state;
  // (The network's deliveries land at the END OF THE NIGHT since playtest
  // 13, so the dawn split sees them: see applyContinue, 'income'.)
  const broughtLine = '';
  let dealtLine = '';
  if (hallOf(civ)) {
    const n = dealFor(hands.culture, modsOf(civ).drawLess);
    let h;
    [h, rng] = drawCards(hallOf(civ), n, rng);
    civ = { ...civ, hall: h };
    dealtLine = hands.culture
      ? `; the Hall is dealt ${n} ${n === 1 ? 'card' : 'cards'} for ${hands.culture} ${hands.culture === 1 ? 'worker' : 'workers'}`
      : '; the Hall, given no workers, is dealt no cards';
    hands.culture = 0;
  }
  let next = {
    ...state,
    rng,
    allocation,
    hands,
    done: emptyByRole(false),
    taken: emptyByRole(null),
    boons: emptyByRole(0),
    undoStack: {},
    civ,
    meta: { ...state.meta, phase: PHASES.DAY },
  };
  const split = ROLES.map((r) => `${ROLE_INFO[r].label} ${allocation[r]}${boons[r] ? ` (+${boons[r]} ${boons[r] === 1 ? 'student' : 'students'} of the Scholars)` : ''}`).join(', ');
  next = logLine(next, { kind: 'order', text: `Round ${state.meta.round}: ${split}${idle ? `, ${idle} idle` : ''}${dealtLine}${broughtLine}.` });
  return bump(next);
};

// ------------------------------------------------------------------ UNDO
// Matt, 2026-09-22: "A player should be able to undo anything unless there
// is new information learned... a die roll, exposing a new tile, mixing
// elements, giving things to other players. If an action contributes to a
// common pool it can be undone unless doing so would need to remove more
// of something than remains in the pool."
//
// So every act pushes an entry on the role's own STACK with a snapshot of
// the state before and after, and a `blocked` reason if it showed
// something. Taking one back does not restore the old state (the other
// three have been playing since) - it UNWINDS: whatever this act changed
// and nobody has touched since goes back to how it was; the common pool
// gives back what was gained and returns what was paid, if it can; and
// anything somebody else has changed since refuses, with a reason.
const UNDO_DEPTH = 30;
const POOL = new Set(['food', 'goods', 'coin', 'knowledge', 'happiness', 'population']);
const NOT_COMPARED = new Set(['workers', 'threat']);
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const snapshot = (state, role) => ({
  civ: Object.fromEntries(Object.entries(state.civ).filter(([k]) => !NOT_COMPARED.has(k))),
  hands: (state.hands || {})[role] || 0,
  taken: (state.taken || {})[role] || {},
  deck: state.deck, discard: state.discard, regionDeck: state.regionDeck, removed: state.removed,
});
export const undoStackOf = (state, role) => (state.undoStack || {})[role] || [];
/** Drop every part of a before/after pair that the act did not change. */
const slimPair = (before, after) => {
  const b = { ...before, civ: {} };
  const a = { ...after, civ: {} };
  for (const key of new Set([...Object.keys(before.civ), ...Object.keys(after.civ)])) {
    if (same(before.civ[key], after.civ[key])) continue;
    if (key in before.civ) b.civ[key] = before.civ[key];
    if (key in after.civ) a.civ[key] = after.civ[key];
  }
  for (const key of ['deck', 'discard', 'regionDeck', 'removed']) {
    if (same(before[key], after[key])) { delete b[key]; delete a[key]; }
  }
  return [b, a];
};

/** Unwind one entry against the state as it is now. Returns [state | null, why]. */
const unwind = (state, entry, role) => {
  const { before, after } = entry;
  const civ = { ...state.civ };
  for (const key of new Set([...Object.keys(before.civ), ...Object.keys(after.civ)])) {
    const cur = state.civ[key];
    const was = before.civ[key];
    const then = after.civ[key];
    if (same(then, was)) continue; // this act did not touch it
    if (same(cur, then)) { civ[key] = was; continue; } // untouched since: back it goes
    if (POOL.has(key) && typeof cur === 'number') {
      const delta = (then || 0) - (was || 0);
      if (cur - delta < 0) return [null, `the ${key} it made has been spent`];
      civ[key] = cur - delta;
      continue;
    }
    return [null, 'someone else has changed that since'];
  }
  const top = {};
  for (const key of ['deck', 'discard', 'regionDeck', 'removed']) {
    if (same(after[key], before[key])) continue;
    if (!same(state[key], after[key])) return [null, 'the deck has moved on since'];
    top[key] = before[key];
  }
  return [{
    ...state,
    ...top,
    civ: syncThreat(civ),
    // As a delta, not a restore: workers the Hall handed this role since are kept.
    hands: { ...(state.hands || {}), [role]: Math.max(0, ((state.hands || {})[role] || 0) - (after.hands - before.hands)) },
    taken: { ...(state.taken || {}), [role]: before.taken },
    log: state.log.filter((l) => l.seq !== entry.seq || l.kind !== 'act' || l.role !== role),
  }, ''];
};

/** Can this role take its last act back right now, and if not, why not. */
export const undoCheck = (state, role) => {
  if (state.meta.phase !== PHASES.DAY) return { ok: false, why: 'The day is over.' };
  if (state.done?.[role]) return { ok: false, why: 'You are done for the day.' };
  const stack = undoStackOf(state, role);
  const top = stack[stack.length - 1];
  if (!top) return { ok: false, why: 'Nothing to take back.' };
  if (top.blocked) return { ok: false, why: `Not after ${top.blocked}.`, label: top.label };
  const [next, why] = unwind(state, top, role);
  if (!next) return { ok: false, why: `Not now: ${why}.`, label: top.label };
  return { ok: true, why: '', label: top.label };
};

const applyUndo = (state, { role }) => {
  if (!undoCheck(state, role).ok) return state;
  const stack = undoStackOf(state, role);
  const top = stack[stack.length - 1];
  const [next] = unwind(state, top, role);
  const undoStack = { ...(state.undoStack || {}), [role]: stack.slice(0, -1) };
  return bump(logLine({ ...next, undoStack }, { kind: 'undo', role, text: `${ROLE_INFO[role].label} takes back ${top.label}.` }));
};

/** A role is finished for the day. When all are, the day's work is tallied. */
const applyDone = (state, { role }) => {
  if (state.meta.phase !== PHASES.DAY || !ROLES.includes(role) || state.done?.[role]) return state;
  const done = { ...(state.done || emptyByRole(false)), [role]: true };
  let next = { ...state, done };
  if (ROLES.every((r) => done[r])) next = { ...next, meta: { ...next.meta, phase: PHASES.TALLY } };
  return bump(next);
};

/** Not done after all: only while the day is still on (once everyone is done, the tally has begun). */
const applyUndone = (state, { role }) => {
  if (state.meta.phase !== PHASES.DAY || !ROLES.includes(role) || !state.done?.[role]) return state;
  return bump({ ...state, done: { ...state.done, [role]: false } });
};

const applyAct = (state, { role, actionId, params = {} }) => {
  if (state.meta.phase !== PHASES.DAY || state.done?.[role]) return state;
  const check = checkAction(state, role, actionId, params);
  if (!check.ok || check.needsParam) return state;
  // Remember where we were, so the same role can take this one back
  // (Matt: "I'm tapping something and there's no way to undo it").
  const before = snapshot(state, role);

  const action = findAction(role, actionId);
  const seen = civFor(state, role);
  const times = Number.isInteger(params.times) && capacity(action, seen) > 1 ? params.times : 1;
  const cost = resolveCost(action, seen, times, params, state);
  const label = times > 1 ? `${resolveLabel(action, seen)} ×${times}` : resolveLabel(action, seen);
  // During the day the role spends ITS hands; `civ.workers` here is that.
  const idle = state.civ.workers;
  let civ = seen;
  let lines = [];
  let next = state;

  [civ] = applyPatch(civ, costPatch(cost));
  // What the board tells you afterwards is measured from HERE: after the
  // price is paid, before anything is gained. Matt, 2026-09-19: "I think I
  // just hunted, but it didn't tell me what actually happened."
  const startedWith = countsOf(civ);
  // A repeated action is the same action `times` over: the patches add up
  // (and a caravan sent thrice leaves three cards behind).
  const outcome = action.apply(civ, params) || {};
  if (times > 1 && outcome.patch) {
    outcome.patch = Object.fromEntries(Object.entries(outcome.patch).map(([k, n]) => [k, n * times]));
  }
  // Dice: one roll per time, written down so the table can show them.
  let rolls = null;
  // What this die's sides ARE, so the board can draw a three-sided die
  // with three faces on it instead of a d6 flashing numbers it cannot roll.
  let sides = null;
  if (action.dice) {
    // The die may depend on WHICH unit is acting - a forage is paid by the
    // ground that unit is standing on - so the params come through too.
    const die = action.dice(seen, action.needsParam ? params : undefined);
    sides = Array.from({ length: die.faces }, (_, i) => die.base + i + 1);
    rolls = [];
    let { rng } = next;
    const count = die.count ?? times;
    for (let i = 0; i < count; i += 1) {
      let face;
      [face, rng] = nextInt(die.faces, rng);
      rolls.push(die.base + face + 1);
    }
    next = { ...next, rng };
    outcome.patch = { ...(outcome.patch || {}), [die.resource]: rolls.reduce((a, b) => a + b, 0) };
    lines.push(`rolled ${rolls.join(' + ')}`);
  }

  if (outcome.patch) {
    let gained;
    [civ, gained] = applyPatch(civ, outcome.patch);
    lines = lines.concat(gained);
  }
  if (outcome.tech) {
    const tech = TECHS[outcome.tech];
    civ = { ...civ, techs: [...civ.techs, outcome.tech] };
    lines.push(`learned ${tech.name}`);
    civ = dealOpened(civ, outcome.tech, lines);
    if (tech.removes) {
      const before = next.deck.length;
      next = removeCards(next, tech.removes);
      const n = before - next.deck.length;
      if (n) lines.push(`${n} ${n === 1 ? 'card' : 'cards'} removed from the deck`);
    }
  }
  let explored = null;
  // WHERE IT HAPPENED, and for a fight HOW: the table's camera flies there
  // and stages it (playtest 12, Matt: "it'd be fun if for a battle it zoomed
  // in before the battle and then we saw a little fight... it took enough
  // time for us to notice it"). The log line said what; this says where.
  let stage = null;
  const actor = params.unit ? unitById(civ, params.unit) : null;
  if (actor) stage = { unit: actor.id, kind: actor.kind || 'warband', from: [actor.q, actor.r], at: [actor.q, actor.r] };
  const battleOf = (u, foe, out, foeKind, foeFigure) => ({
    at: [foe.q, foe.r],
    from: [u.q, u.r],
    ours: { id: u.id, kind: u.kind || 'warband', before: u.strength, after: out.ours, dice: out.mine, hitOn: ourHitOn(civ, u) },
    theirs: { id: foe.id, kind: foeKind, figure: foeFigure, before: foe.strength ?? foe.power, after: out.foe, dice: out.theirs, hitOn: BAND.hitOn },
  });
  // ---- the Frontier's plan. Every one of these costs ONE ORDER, and an
  // order is a worker: a worker is one unit's turn. See engine/units.js.
  if (outcome.layRoad) {
    const u = unitById(civ, outcome.layRoad.unit);
    if (u && canLayRoad(civ, u.q, u.r)) {
      civ = { ...civ, roads: [...(civ.roads || []), `${u.q},${u.r}`] };
      lines.push(`${describeUnit(u)} lays road at ${placeName(civ, u.q, u.r)}`);
      const joined = villagesOf(civ).filter((v) => !v.damaged && roadBonus(civ, v) && !roadBonus({ ...civ, roads: (civ.roads || []).slice(0, -1) }, v));
      joined.forEach((v) => lines.push(`${v.name} is joined to the city by road: +${roadLevel(civ)} a night`));
    }
  }
  if (outcome.moveUnit) {
    const { unit, to: raw } = outcome.moveUnit;
    const u = unitById(civ, unit);
    const to = { q: Number(raw.q), r: Number(raw.r) };
    if (u) {
      civ = { ...civ, units: unitsOf(civ).map((x) => (x.id === u.id ? { ...x, q: to.q, r: to.r } : x)) };
      const moved = unitById(civ, u.id);
      civ = reveal(civ, to.q, to.r, seenFrom(civ, moved));
      lines.push(`${describeUnit(moved)} moves to ${placeName(civ, to.q, to.r)}`);
      // Walk onto something and it is yours. Matt: "It'd be fun to find like
      // ancient ruins and things when I'm exploring the map."
      const found = findAt(civ, to.q, to.r);
      if (found) {
        const what = FINDS[found.kind];
        civ = { ...civ, finds: (civ.finds || []).filter((f) => f.id !== found.id), findsTaken: [...(civ.findsTaken || []), hexKey(found.q, found.r)] };
        let got;
        [civ, got] = applyPatch(civ, what.gain);
        lines.push(`${what.name.toLowerCase()}`, ...got);
      }
      // Unknown ground: whatever was dealt to this site rises from the sea.
      const site = siteAt(to.q, to.r, civ);
      const regionId = site >= 0 ? sitesOf(civ)[site] : null;
      if (regionId && !civ.regions.includes(regionId)) {
        const region = REGIONS[regionId];
        let gained;
        [civ, gained] = applyPatch(civ, region.bonus);
        civ = { ...civ, regions: [...civ.regions, regionId] };
        next = { ...next, regionDeck: next.regionDeck.filter((id) => id !== regionId) };
        explored = { direction: site, regionId };
        lines.push(`found ${region.name}`);
        lines = lines.concat(gained);
        // THE NEIGHBOURS ARE SOMEBODY (playtest 9: "we discovered neighbours...
        // it was really unclear on the map what they were or what they meant").
        // Finding the region is meeting the nearest people you have not met:
        // their land comes into sight and their envoys are on the way.
        const people = region.meets ? nearestUnmet(civ, to.q, to.r) : null;
        if (people) {
          civ = {
            ...civ,
            seen: [...new Set([...(civ.seen || []), ...(people.held || [])])],
            rivals: rivalsOf(civ).map((x) => (x.id === people.id ? { ...x, met: true } : x)),
            rivalMeeting: people.id,
            rivalCards: [...(civ.rivalCards || []), { card: 'rival_envoys', rival: people.id }],
          };
          next = seedNext({ ...next, civ }, 'rival_envoys');
          civ = next.civ;
          lines.push(`the smoke is ${people.name}'s - their land is marked on the map, and their envoys are on the way`);
          explored = { ...explored, met: people.id };
        } else if (region.adds) {
          // The far ring's regions have no story card (yet).
          next = seedCard({ ...next, civ }, region.adds);
          civ = next.civ;
          lines.push('a card was added to the deck');
        }
        if (region.unlocks) {
          let opened;
          [next, opened] = openBranch({ ...next, civ }, region.unlocks, region.name);
          civ = next.civ;
          if (opened) lines.push(opened);
        }
        if (region.reveals) {
          let said;
          [civ, said] = revealAspect(civ, region.reveals[0], region.reveals[1]);
          if (said) lines.push(`the Scholars learn from ${region.name}: ${said}`);
        }
      }
    }
  }
  if (outcome.fightWith) {
    let said;
    let rng;
    const u0 = unitById(civ, outcome.fightWith.unit);
    const foe0 = (civ.menaces || []).find((m) => m.id === outcome.fightWith.foe) || raidersOf(civ).find((x) => x.id === outcome.fightWith.foe);
    let out;
    [civ, said, rolls, rng, out] = resolveFight(civ, outcome.fightWith, next.rng);
    next = { ...next, rng };
    if (u0 && foe0 && out) stage = { ...stage, at: [foe0.q, foe0.r], battle: battleOf(u0, foe0, out, foe0.kind || 'raiders', 'raider') };
    lines = lines.concat(said);
    sides = [1, 2, 3, 4, 5, 6];
  }
  if (outcome.merge) {
    const u = unitById(civ, outcome.merge.unit);
    const into = unitById(civ, outcome.merge.into);
    if (u && into) {
      civ = { ...civ, units: unitsOf(civ).filter((x) => x.id !== u.id).map((x) => (x.id === into.id ? { ...x, strength: x.strength + u.strength, wins: Math.max(x.wins || 0, u.wins || 0) } : x)) };
      lines.push(`${describeUnit(u, civ)} joins ${unitName(into)}: ${into.strength + u.strength} strong`);
      stage = { ...stage, at: [into.q, into.r] };
    }
  }
  if (outcome.parley) {
    const u = unitById(civ, outcome.parley.unit);
    const camp = (civ.menaces || []).find((m) => m.id === outcome.parley.foe);
    const rival = rivalById(civ, outcome.parley.foe);
    // The die first: the coin is spent either way.
    let face;
    let { rng } = next;
    [face, rng] = nextInt(6, rng);
    face += 1;
    next = { ...next, rng };
    rolls = [face];
    sides = [1, 2, 3, 4, 5, 6];
    const took = face >= PARLEY_HOLDS_ON;
    if (u && !took) {
      // A refusal gives the coin back (playtest 14: "I took my coin and they
      // didn't go away, those jerks"): the gamble costs the action, not the purse.
      const back = outcome.parley.paid?.coin || 0;
      if (back) civ = { ...civ, coin: civ.coin + back };
      lines.push(`rolled ${face}: ${camp ? `the ${camp.kind} will not hear of it and stays` : `${rival?.name || 'they'} will not hear of it and are no warmer`}${back ? ` - the ${back} coin comes back` : ''}`);
    } else if (u && camp) {
      civ = syncThreat({ ...civ, menaces: (civ.menaces || []).filter((m) => m.id !== camp.id) });
      lines.push(`rolled ${face}: the ${camp.kind} takes the coin and breaks up`);
    } else if (u && rival) {
      const steps = wayOf(rival).warmBy + (modsOf(civ).envoyTwice ? 1 : 0);
      let stance = rival.stance;
      for (let i = 0; i < steps; i += 1) stance = warmer(stance);
      civ = syncThreat({ ...civ, rivals: rivalsOf(civ).map((r) => (r.id === rival.id ? { ...r, stance } : r)) });
      lines.push(`rolled ${face}: ${stance === rival.stance ? `${rival.name} are already as warm as they get` : `${rival.name} are ${stance} now`}`);
    }
  }
  if (outcome.raid) {
    const u = unitById(civ, outcome.raid.unit);
    const rival = rivalById(civ, outcome.raid.foe);
    if (u && rival) {
      let out;
      let rng;
      [out, rng] = exchange(civ, u, { strength: rival.power }, next.rng);
      next = { ...next, rng };
      const front = (rival.held || []).map((k) => k.split(',').map(Number)).sort((a, b) => hexDistanceBetween(a, [u.q, u.r]) - hexDistanceBetween(b, [u.q, u.r]))[0] || [rival.q, rival.r];
      stage = { ...stage, at: front, battle: battleOf(u, { id: rival.id, q: front[0], r: front[1], power: rival.power }, out, 'rival', 'raider') };
      rolls = out.mine;
      sides = [1, 2, 3, 4, 5, 6];
      lines.push(`${describeUnit(u)} rolled ${out.mine.join(' + ')}: ${out.ourHits} ${out.ourHits === 1 ? 'hit' : 'hits'}`, `${rival.name} rolled ${out.theirs.join(' + ')}: ${out.theirHits} back`);
      if (out.ours <= 0) {
        civ = { ...civ, units: unitsOf(civ).filter((x) => x.id !== u.id) };
        lines.push(`${describeUnit(u)} is destroyed`);
      } else if (out.ours < u.strength) {
        civ = { ...civ, units: unitsOf(civ).map((x) => (x.id === u.id ? { ...x, strength: out.ours } : x)) };
        lines.push(`down to ${out.ours}`);
      }
      if (out.foe <= 0) {
        // Broken: their land is free ground again, their seat stays, they start over small and wary.
        civ = { ...civ, rivals: rivalsOf(civ).map((r) => (r.id === rival.id ? { ...r, power: RIVAL.startPower, held: [hexKey(r.q, r.r)], stance: 'wary', broken: (r.broken || 0) + 1, age: 0 } : r)) };
        let got;
        [civ, got] = applyPatch(civ, { coin: RIVAL.loot, goods: 2 });
        lines.push(`${rival.name} are broken - their land is open again`, ...got);
      } else {
        const power = out.foe;
        // The Reed People are slow to anger: one step colder, not straight to war.
        const stance = power <= RIVAL.peaceAt ? 'wary' : (wayOf(rival).slowAnger ? colder(rival.stance) : 'hostile');
        civ = { ...civ, rivals: rivalsOf(civ).map((r) => (r.id === rival.id ? { ...r, power, stance } : r)) };
        lines.push(`${rival.name} hold at ${power}${stance === 'hostile' ? ' and stay hostile' : ' and ask for peace'}`);
      }
      civ = syncThreat(civ);
    }
  }
  if (outcome.foundVillage) {
    const u = unitById(civ, outcome.foundVillage.unit);
    if (u) {
      const id = `v${(civ.villageSeq || 0) + 1}`;
      const taken = new Set(villagesOf(civ).map((v) => v.name));
      // Drawn at random from the ones not yet used (playtest 13: the first
      // two were always Highfold and Ashby), from the game's own seed.
      const free = VILLAGE_NAMES.filter((n) => !taken.has(n));
      let name = `Village ${(civ.villageSeq || 0) + 1}`;
      if (free.length) {
        let ni;
        let { rng } = next;
        [ni, rng] = nextInt(free.length, rng);
        next = { ...next, rng };
        name = free[ni];
      }
      const village = {
        id, name, q: u.q, r: u.r, size: VILLAGE.startSize, age: 0, damaged: false, raids: 0, chartered: false, order: 'food',
      };
      // The pioneer is SPENT doing it.
      civ = {
        ...civ,
        villages: [...villagesOf(civ), village],
        villageSeq: (civ.villageSeq || 0) + 1,
        units: unitsOf(civ).filter((x) => x.id !== u.id),
      };
      civ = reveal(civ, u.q, u.r, 1);
      const w = workAt(civ, u.q, u.r);
      lines.push(`a village, ${name}, stands at ${placeName(civ, u.q, u.r)}`, w ? `it will send up ${w.resource}` : 'it will send up what it can');
      // Founding a village puts ITS charter into the HALL's deck - not the
      // event deck; it is a card the Hall plays. One per village, so
      // trashing one costs that town and never the mechanic itself.
      civ = grieve(civ, 'charter');
      lines.push('a Charter goes into the Hall\'s deck');
    }
  }
  if (outcome.repairVillage) {
    const u = unitById(civ, outcome.repairVillage.unit);
    const v = u ? villageAt(civ, u.q, u.r) : null;
    if (v) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, damaged: false } : x)) };
      lines.push(`${v.name} is put back together`);
    }
  }
  if (outcome.forage) {
    // The dice themselves are rolled by the generic path above, which knows
    // about `action.dice`. All this adds is WHERE it happened.
    const u = unitById(civ, outcome.forage.unit);
    if (u) lines.push(`at ${placeName(civ, u.q, u.r)}`);
  }
  // ---- the Stewards' day
  if (outcome.train) {
    const kind = UNIT_KINDS[outcome.train.kind] ? outcome.train.kind : 'warband';
    const spot = musterPoints(civ).find((m) => (outcome.train.at ? m.id === outcome.train.at : true))
      || musterPoints(civ)[0];
    if (spot) {
      const id = `u${(civ.unitSeq || 0) + 1}`;
      const barracks = villagesOf(civ).some((v) => v.city && v.district === 'barracks' && v.q === spot.q && v.r === spot.r);
      const strength = kind === 'warband'
        ? Math.min(warbandCap(civ), UNIT_KINDS.warband.startStrength + (barracks ? 1 : 0))
        : UNIT_KINDS[kind].startStrength;
      civ = {
        ...civ,
        units: [...unitsOf(civ), {
          id, kind, q: spot.q, r: spot.r, strength,
        }],
        unitSeq: (civ.unitSeq || 0) + 1,
      };
      lines.push(`a ${UNIT_KINDS[kind].name.toLowerCase()} musters at ${spot.name}`);
    }
  }
  if (outcome.reinforce) {
    const u = unitById(civ, outcome.reinforce.unit);
    if (u) {
      civ = { ...civ, units: unitsOf(civ).map((x) => (x.id === u.id ? { ...x, strength: Math.min(warbandCap(civ), x.strength + 1) } : x)) };
      lines.push(`${describeUnit(unitById(civ, u.id))} comes back up to strength`);
    }
  }
  if (outcome.work) {
    const city = cityOf(civ);
    const b = city.buildings.find((x) => x.id === outcome.work);
    if (b) {
      const buildings = city.buildings.map((x) => (x.id === b.id ? { ...x, staffed: x.staffed + 1 } : x));
      civ = { ...civ, city: { ...city, buildings } };
      // An upgraded field (an orchard) or workshop (a foundry) makes one more.
      const more = b.upgraded ? 1 : 0;
      const made = b.type === 'field' ? { food: fieldFood(civ) + more }
        : b.type === 'workshop' ? { goods: workshopGoods(civ) + more }
          : b.type === 'market' ? { goods: -marketSale(civ).goods, coin: marketSale(civ).coin } : {};
      if (b.type === 'market' && civ.goods < marketSale(civ).goods) {
        lines.push('the market has nothing to sell');
      } else {
        let got;
        [civ, got] = applyPatch(civ, made);
        lines.push(`a worker works the ${BUILDINGS[b.type].name.toLowerCase()}`, ...got);
      }
    }
  }
  if (outcome.build) {
    const city = cityOf(civ);
    const hex = city.hexes.find((h) => (6 - city.buildings.filter((x) => x.hex[0] === h[0] && x.hex[1] === h[1]).reduce((n, x) => n + (BUILDINGS[x.type]?.plots || 1), 0)) >= BUILDINGS[outcome.build].plots);
    if (hex) {
      const id = `b${(city.seq || 0) + 1}`;
      civ = syncCity({ ...civ, city: { ...city, seq: (city.seq || 0) + 1, buildings: [...city.buildings, { id, type: outcome.build, hex: hex.slice(), staffed: 0, burned: false }] } });
      lines.push(`a ${BUILDINGS[outcome.build].name.toLowerCase()} stands at ${hex[0]},${hex[1]}`);
    }
  }
  // THE CARAVAN GOES SOMEWHERE NOW. Matt, 2026-09-20: "this could be a way
  // to replace the trade as just sort of it ambiguously goes out into the
  // world - we don't know to whom." It visits a named village, brings back
  // a full load instead of the nightly trickle, and feeds it so it grows.
  if (outcome.visit) {
    const v = villagesOf(civ).find((x) => x.id === outcome.visit);
    if (v && !v.damaged) {
      const w = villageYield(civ, v);
      const kind = Object.keys(w)[0] || 'goods';
      let got;
      // The settlement's own yield (road included), not a flat load.
      [civ, got] = applyPatch(civ, { [kind]: caravanLoad(civ, v) });
      // The food it was fed makes it grow: a village to VILLAGE.maxSize, a
      // town on to TOWN.maxSize (a city grows on its own).
      const grown = v.city ? v.size : Math.min(v.chartered ? TOWN.maxSize : VILLAGE.maxSize, v.size + 1);
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, size: grown } : x)) };
      lines.push(`a caravan reaches ${v.name}`, ...got,
        grown > v.size ? `${v.name} is fed and grows to ${grown}` : `${v.name} is already as big as it gets`);
    }
  }
  if (outcome.order) {
    const { village, kind } = outcome.order;
    const v = villagesOf(civ).find((x) => x.id === village);
    if (v) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, order: kind } : x)) };
      lines.push(`${v.name} will send ${kind} each night`);
    }
  }
  // THE BRONZE AGE'S VERBS.
  if (outcome.upgrade) {
    const city = cityOf(civ);
    const b = city.buildings.find((x) => x.id === outcome.upgrade);
    if (b) {
      civ = syncCity({ ...civ, city: { ...city, buildings: city.buildings.map((x) => (x.id === b.id ? { ...x, upgraded: true } : x)) } });
      lines.push(`the ${BUILDINGS[b.type].name.toLowerCase()} becomes ${b.type === 'field' ? 'an' : 'a'} ${UPGRADES[b.type].toLowerCase()}: a shift there makes one more`);
    }
  }
  if (outcome.fort) {
    const u = unitById(civ, outcome.fort);
    if (u) {
      civ = { ...civ, forts: [...(civ.forts || []), hexKey(u.q, u.r)] };
      lines.push(`${describeUnit(u, civ)} raise a fort at ${placeName(civ, u.q, u.r)}: raiders cannot pass it`);
      stage = { ...stage, at: [u.q, u.r] };
    }
  }
  if (outcome.treaty) {
    const r = rivalById(civ, outcome.treaty);
    if (r) {
      civ = { ...civ, rivals: rivalsOf(civ).map((x) => (x.id === r.id ? { ...x, allied: true } : x)) };
      lines.push(`a treaty with ${r.name}: allies now - their carts come twice as full, and they will not raid`);
    }
  }
  if (outcome.survey) {
    const t = surveyTarget(civ);
    if (t) {
      t.hexes.forEach(([q, r]) => { civ = reveal(civ, q, r, 1); });
      lines.push(`the survey maps ${REGIONS[(civ.regionSites || {})[NEAR_SITES + t.i]]?.name || 'a far region'} - its ground is in sight now`);
    }
  }
  // CITIES (CITY in constants.js).
  if (outcome.raiseCity) {
    const v = villagesOf(civ).find((x) => x.id === outcome.raiseCity.village);
    if (v) {
      civ = { ...civ, population: Math.max(1, civ.population - CITY.settlers), villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, city: true, focus: 'goods', district: null } : x)) };
      lines.push(`${v.name} becomes a city: ${CITY.settlers} people leave the capital to settle it`, 'it makes its size in goods until you set its focus');
    }
  }
  if (outcome.cityFocus) {
    const v = villagesOf(civ).find((x) => x.id === outcome.cityFocus.village);
    if (v) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, focus: outcome.cityFocus.kind } : x)) };
      lines.push(outcome.cityFocus.kind === 'defence' ? `${v.name} looks to its walls` : `${v.name} will make ${outcome.cityFocus.kind}`);
    }
  }
  if (outcome.cityDistrict) {
    const v = villagesOf(civ).find((x) => x.id === outcome.cityDistrict.village);
    const d = CITY.districts[outcome.cityDistrict.district];
    if (v && d) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, district: outcome.cityDistrict.district } : x)) };
      lines.push(`${v.name} builds its ${d.name.toLowerCase()}: ${d.text}`);
    }
  }
  // The Hall charters a village. One Charter card per village founded, so
  // culling one costs THAT town and never the mechanic itself.
  if (outcome.charter) {
    const v = villagesOf(civ).find((x) => !x.chartered && x.size >= VILLAGE.charterAtSize)
      || villagesOf(civ).find((x) => !x.chartered);
    if (v) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, chartered: true, order: 'food' } : x)) };
      lines.push(`${v.name} is chartered as a town`, 'it fills a standing order now, and it eats');
    }
  }
  if (outcome.order) {
    const { village, kind } = outcome.order;
    if (TOWN.orders.includes(kind)) {
      civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === village ? { ...x, order: kind } : x)) };
      const v = villagesOf(civ).find((x) => x.id === village);
      if (v) lines.push(`${v.name} will make ${kind} from now on`);
    }
  }
  if (outcome.annex) {
    const city = cityOf(civ);
    const hex = [Number(outcome.annex.q), Number(outcome.annex.r)];
    civ = { ...civ, city: { ...city, hexes: [...city.hexes, hex] } };
    civ = reveal(civ, hex[0], hex[1], 1);
    lines.push(`the city takes in ${hex[0]},${hex[1]}`);
  }
  if (outcome.repair) {
    const city = cityOf(civ);
    const buildings = city.buildings.map((x) => (x.id === outcome.repair ? { ...x, burned: false } : x));
    const b = city.buildings.find((x) => x.id === outcome.repair);
    civ = syncCity({ ...civ, city: { ...city, buildings } });
    if (b) lines.push(`the ${BUILDINGS[b.type].name.toLowerCase()} is repaired`);
  }
  // ---- the Scholars' day
  if (outcome.mix) {
    const { a, b, target } = outcome.mix;
    const got = mixNatures(civ, a, b);
    const lore = loreOf(civ);
    const aiming = Boolean(target && TECHS[target]);
    const need = aiming ? (lore.reactions[target] || {}) : null;
    const hit = aiming && satisfies(got, need);
    // An aimed experiment is a BET (Matt, 2026-09-19): "in order to earn an
    // actual technology, you have to predict it by aiming at it. And when
    // you aim at it, if you don't hit it, you learn nothing." So a miss
    // records no properties at all - only that this pair cannot make that
    // technology, which the notebook then uses to rule natures out.
    const entry = !aiming
      ? { a, b, shared: got, round: state.meta.round }
      : (hit
        ? { a, b, shared: got, target, eureka: true, round: state.meta.round }
        : { a, b, target, failed: true, round: state.meta.round });
    civ = patchLore(civ, { experiments: [...(lore.experiments || []), entry] });
    if (!aiming) lines.push(`mixes ${PRINCIPLE_NAMES[a]} and ${PRINCIPLE_NAMES[b]}: ${describeShared(got)}`);
    else if (!hit) lines.push(`aims ${PRINCIPLE_NAMES[a]} and ${PRINCIPLE_NAMES[b]} at ${TECHS[target].name} - the reaction fails, and nothing is learned but that those two cannot make it`);
    else lines.push(`mixes ${PRINCIPLE_NAMES[a]} and ${PRINCIPLE_NAMES[b]}: ${describeShared(got)}`);
    if (aiming) {
      if (hit) {
        lines.push(`EUREKA - that is the reaction of ${TECHS[target].name}; it is learned`);
        [civ, next] = learnTech(civ, next, target, lines);
      }
    }
  }
  // THE LADDER (2026-10-15): two things into a machine, one thing out onto
  // the shelf, its nature shown. The inputs are used up (the eight are
  // bought with the fuel's goods; a glaze or an essence comes off the shelf).
  if (outcome.fire) {
    const { machine, a, b } = outcome.fire;
    const m = machineById(machine);
    const law = lawOf(civ, machine);
    const first = inputNature(civ, machine, a);
    const second = inputNature(civ, machine, b);
    if (m && law && first && second) {
      const outKey = natureKey(fireNatures(law, first, second));
      let lore = loreOf(civ);
      if (m.from) lore = shelfAdd(shelfAdd(lore, m.from, a, -1), m.from, b, -1);
      lore = shelfAdd(lore, machine, outKey, 1);
      const firings = { ...(lore.firings || {}), [machine]: [...(lore.firings?.[machine] || []), { a, b, out: outKey, round: state.meta.round }] };
      civ = { ...civ, lore: { ...lore, firings } };
      lines.push(`feeds the ${m.name} ${inputName(machine, a)}, then ${inputName(machine, b)}: out comes a ${productName(machine, outKey)}`);
    }
  }
  if (outcome.make) {
    const { target } = outcome.make;
    const t = TECHS[target];
    const key = t ? fittingProduct(civ, t.tier, loreOf(civ)?.reactions?.[target]) : null;
    const m = t ? MACHINES.find((x) => x.tier === t.tier) : null;
    if (key && m) {
      civ = { ...civ, lore: shelfAdd(loreOf(civ), m.id, key, -1) };
      lines.push(`EUREKA - a ${productName(m.id, key)} is what ${t.name} needs; it is learned`);
      [civ, next] = learnTech(civ, next, target, lines);
    }
  }
  if (outcome.publish) {
    const { principle, nature, teach } = outcome.publish;
    const lore = loreOf(civ);
    const to = ROLES.includes(teach) ? teach : null;
    civ = patchLore(civ, { published: { ...(lore.published || {}), [principle]: { ...nature, round: state.meta.round, teach: to } } });
    lines.push(`publishes: ${PRINCIPLE_NAMES[principle]} is ${describeNature(nature)} - the night will tell${to ? `; if it holds, the ${ROLE_INFO[to].label} learn a worker's worth` : ''}`);
  }
  if (outcome.hint) {
    const p = outcome.hint;
    const unknown = ASPECTS.filter((k) => knownOf(civ, p)[k] === undefined);
    if (unknown.length) {
      let ai;
      let { rng } = next;
      [ai, rng] = nextInt(unknown.length, rng);
      next = { ...next, rng };
      let said;
      [civ, said] = revealAspect(civ, p, unknown[ai]);
      if (said) lines.push(`the stones say: ${said}`);
    }
  }

  // ---- the Hall's day
  let handsGiven = null;
  if (outcome.play) {
    const id = outcome.play;
    const card = HALL_CARDS[id];
    const hall = hallOf(civ);
    if (card && hall && hall.hand.includes(id)) {
      const seat = outcome.counselor;
      const person = COUNSELORS[seat];
      const seatName = person?.name || 'the Hall';
      const hand = hall.hand.slice();
      hand.splice(hand.indexOf(id), 1);
      // A seat takes one card a day.
      const used = { ...(hall.used || {}), ...(seat ? { [seat]: true } : {}) };
      // The bonuses ride on the play, read BEFORE this card joins today's
      // played list (a song does not pair with itself).
      const bonuses = bonusesFor(civ, id, seat);
      const liked = bonuses.find((b) => b.who);
      const pair = bonuses.find((b) => b.pair);
      const effect = cardEffect(civ, id, seat);
      const draws = cardDraws(civ, id, seat);
      const give = cardGives(civ, id, seat);
      const toBand = sendsToBand(civ, id, seat);
      // A law is played once and stands forever; everything else comes
      // around. Today's plays are remembered for the pair-ups.
      const played = [...(hall.played || []), id];
      civ = {
        ...civ,
        hall: card.law
          ? { ...hall, hand, laws: [...hall.laws, id], used, played }
          : { ...hall, hand, discard: [...hall.discard, id], used, played },
      };
      let got;
      [civ, got] = applyPatch(civ, effect);
      lines.push(liked ? `gives ${card.name} to ${seatName}, who likes it` : `gives ${card.name} to ${seatName}`, ...got);
      if (pair) lines.push(`${card.name} pairs up: ${pair.text}`);
      if (card.grievance) lines.push('a grievance is settled');
      if (card.law) lines.push(`${card.name} stands as law`);
      // The Charter names a village and makes it a town. One card per
      // village founded, so the Hall may trash one and lose THAT town.
      if (card.charter) {
        // The Hall NAMES the village (playtest 13: two Charters, and the
        // play picked Ashby on its own - "it would've been nice if we could've
        // picked"). A play without one (a bot, an old phone) takes the first
        // that has grown enough.
        const eligible = villagesOf(civ).filter((x) => !x.chartered && x.size >= VILLAGE.charterAtSize);
        const pick = eligible.find((x) => x.id === outcome.village) || eligible[0];
        if (pick) {
          civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === pick.id ? { ...x, chartered: true, order: 'food' } : x)) };
          lines.push(`${pick.name} is chartered as a town - it fills a standing order now, and it eats`);
          // Spent: one Charter per village, so the one that made this town
          // does not come round again (playtest Rvz7A - it went to the
          // discard like any card and was back in hand with no village left).
          const h = hallOf(civ);
          const at = h.discard.lastIndexOf(id);
          if (at >= 0) civ = { ...civ, hall: { ...h, discard: [...h.discard.slice(0, at), ...h.discard.slice(at + 1)] } };
        } else {
          lines.push('no village has grown enough to charter; nothing happens');
        }
      }
      if (give && toBand) {
        handsGiven = { to: 'military', n: give };
        lines.push(`${give} ${give === 1 ? 'worker goes' : 'workers go'} to the Frontier (${shiftsFor('military', give)} actions)`);
      } else if (give) {
        // Split among roles (Matt, playtest 7: "let them split them up").
        // `split` = { role: n }; anything it doesn't place goes to `to`.
        const split = outcome.split && typeof outcome.split === 'object' ? outcome.split : null;
        const parts = [];
        let left = give;
        if (split) {
          for (const r of ROLES) {
            const n = Math.max(0, Math.min(left, Math.floor(Number(split[r]) || 0)));
            if (n) { parts.push({ to: r, n }); left -= n; }
          }
        }
        if (left) parts.push({ to: ROLES.includes(outcome.to) ? outcome.to : role, n: left });
        handsGiven = parts;
        parts.forEach(({ to, n }) => lines.push(`${n} ${n === 1 ? 'worker goes' : 'workers go'} to the ${ROLE_INFO[to].label}`));
      }
      // An idea may be taken up today, because a card said so.
      if (card.buys) {
        civ = { ...civ, hall: { ...hallOf(civ), buys: buysOf(civ) + card.buys } };
        lines.push(card.buys === 1 ? 'one more card may be bought today' : `${card.buys} more cards may be bought today`);
      }
      if (draws) {
        let drawn;
        let { rng } = next;
        [drawn, rng] = drawCards(civ.hall, draws, rng);
        next = { ...next, rng };
        civ = { ...civ, hall: drawn };
        lines.push(`draws ${draws}`);
      }
      const push = cardPush(id, seat, civ, state.meta.round);
      if (push.x || push.y) {
        civ = pushCompass(civ, push);
        lines.push(`the compass moves ${describePush(push)}`);
      }
      if (bonuses.some((b) => b.reveal)) {
        const unknown = PRINCIPLES.flatMap((p) => ASPECTS.filter((a) => loreOf(civ)?.known?.[p]?.[a] === undefined).map((a) => [p, a]));
        if (unknown.length) {
          let ui;
          let { rng } = next;
          [ui, rng] = nextInt(unknown.length, rng);
          next = { ...next, rng };
          let said;
          [civ, said] = revealAspect(civ, unknown[ui][0], unknown[ui][1]);
          if (said) lines.push(`the Scholar reads it out: ${said}`);
        } else {
          // Every nature is known (playtest 13: Nick played through the Scholar
          // in year 3 and Andrew had all eight): the reading is a knowledge instead.
          let more;
          [civ, more] = applyPatch(civ, { knowledge: 1 });
          lines.push(`the Scholar has nothing left to read out - every nature is known - so ${more.join(', ') || 'the reading is kept'}`);
        }
      }
      if (bonuses.some((b) => b.stone) && !civ.stoneLaid) {
        civ = { ...civ, stoneLaid: true };
        lines.push('the Mason lays a stone: the next stage of the great work costs 1 less goods');
      }
      if (card.adds) {
        next = seedCard(next, card.adds);
        // Opera makes every SONG echo twice (its `songTwice` was defined and
        // read by nobody until 2026-09-22).
        const twice = (card.tags || []).includes('song') && modsOf(civ).songTwice > 0;
        if (twice) next = seedCard(next, card.adds);
        lines.push(twice ? 'two cards were added to the deck' : 'a card was added to the deck');
      }
      if (card.envoy) {
        // To the coldest people you have met.
        const met = rivalsOf(civ).filter((r) => r.met).sort((a, b) => ['hostile', 'wary', 'friendly'].indexOf(a.stance) - ['hostile', 'wary', 'friendly'].indexOf(b.stance));
        const to = met[0];
        if (to) {
          const steps = wayOf(to).warmBy + (modsOf(civ).envoyTwice ? 1 : 0);
          let stance = to.stance;
          for (let i = 0; i < steps; i += 1) stance = warmer(stance);
          civ = syncThreat({ ...civ, rivals: rivalsOf(civ).map((r) => (r.id === to.id ? { ...r, stance } : r)) });
          lines.push(`an envoy goes to ${to.name}: they are ${stance} now`);
        } else lines.push('there is nobody to send an envoy to yet');
      }
    }
  }
  if (outcome.buy) {
    const hall = hallOf(civ);
    const id = outcome.buy;
    if (hall && hall.market.includes(id)) {
      let refilled;
      let { rng } = next;
      const market = hall.market.filter((x) => x !== id);
      [refilled, rng] = refillMarket({ ...hall, market, discard: [...hall.discard, id] }, civ, rng);
      next = { ...next, rng };
      civ = { ...civ, hall: { ...refilled, buys: Math.max(0, buysOf(civ) - 1) } };
      lines.push(`adds ${HALL_CARDS[id].name} to the deck`);
    }
  }
  if (outcome.burden) {
    const hall = hallOf(civ);
    const id = hall?.burden;
    if (hall && id && HALL_CARDS[id]?.burden) {
      let got;
      [civ, got] = applyPatch({ ...civ, hall: { ...hall, burden: null, discard: [...hall.discard, id] } }, HALL_CARDS[id].bounty || {});
      lines.push(`takes on ${HALL_CARDS[id].name} for ${got.join(', ') || 'nothing'} - it goes in the deck, and costs 1 happiness a night in hand until settled or trashed`);
    }
  }
  if (outcome.seat) {
    civ = seatFromDoor(civ, outcome.seat, state.meta.round);
    lines.push(`${COUNSELORS[outcome.seat]?.name || 'someone'} takes a chair in the Hall`);
  }
  if (outcome.choose) {
    let { rng } = next;
    const before = councilOf(civ).map((x) => x.id);
    let mootLine;
    [civ, rng, mootLine] = chooseCounselor(civ, outcome.choose, state.meta.round, rng, MOOT_WIN);
    next = { ...next, rng };
    const c = councilOf(civ).find((x) => !before.includes(x.id));
    if (mootLine) lines.push(mootLine);
    if (c) lines.push(`${COUNSELORS[c.id]?.name || 'someone'} takes a chair in the Hall${c.term ? ` for ${c.term} rounds` : ''}`);
    if (c) {
      let shelf;
      let r3 = next.rng;
      [shelf, r3] = refillMarket(civ.hall, civ, r3);
      next = { ...next, rng: r3 };
      const put = shelf.offerBy?.[c.id];
      civ = { ...civ, hall: shelf };
      if (put) lines.push(`${COUNSELORS[c.id].name} puts ${HALL_CARDS[put].name} on the shelf`);
    }
    // Another chair still empty (two retired the same night): put the next
    // candidates forward now rather than a round later (playtest 2026-09-25:
    // "two council members retired and I was only able to replace one").
    if (councilOf(civ).length < COUNCIL_SEATS && !offerOf(civ).length) {
      let offer;
      let r2 = next.rng;
      [offer, r2] = drawOffer(civ, state.meta.round, r2);
      let moot = false;
      if (!offer.length) { [offer, r2] = drawMoot(civ, r2); moot = offer.length > 0; }
      next = { ...next, rng: r2 };
      if (offer.length) {
        civ = { ...civ, hall: { ...civ.hall, offer, moot } };
        lines.push(`${moot ? 'a moot for the other empty chair' : 'for the other empty chair'}: ${offer.map((id) => COUNSELORS[id].name).join(' or ')}`);
      }
    }
  }
  if (outcome.dismiss) {
    civ = dismissCounselor(civ, outcome.dismiss, state.meta.round);
    lines.push(`${COUNSELORS[outcome.dismiss]?.name || 'someone'} is dismissed from the council`);
  }
  if (outcome.cull) {
    const hall = hallOf(civ);
    const id = outcome.cull;
    if (hall && hall.hand.includes(id)) {
      const hand = hall.hand.slice();
      hand.splice(hand.indexOf(id), 1);
      civ = pushCompass({ ...civ, hall: { ...hall, hand } }, { x: 1 });
      lines.push(`${HALL_CARDS[id].name} is trashed`, 'the compass moves toward Novelty');
    }
  }
  if (outcome.monument) {
    const goal = GOALS[goalOf(civ)];
    if (civ.stoneLaid) {
      let back;
      [civ, back] = applyPatch({ ...civ, stoneLaid: false }, { goods: 1 });
      lines.push(`the Mason's stone counts: ${back.join(', ')}`);
    }
    civ = { ...civ, monument: { ...civ.monument, stages: civ.monument.stages + outcome.monument } };
    lines.push(`stage ${civ.monument.stages} of ${goal?.build?.stages ?? MONUMENT_STAGES}`);
    if (goal?.build?.onStage) {
      let extra;
      [civ, extra] = applyPatch(civ, goal.build.onStage);
      lines = lines.concat(extra);
    }
    if (civ.monument.stages === 1) {
      next = seedCard(next, 'anniversary');
      lines.push('a card was added to the deck');
    }
    // The last stage: it stands, it grants, the next vision is dealt.
    let done;
    [next, done] = completeWonder({ ...next, civ });
    civ = next.civ;
    lines = lines.concat(done);
  }
  if (outcome.addCard) {
    for (let i = 0; i < times; i += 1) next = seedCard(next, outcome.addCard);
    lines.push(times > 1 ? `${times} cards were added to the deck` : 'a card was added to the deck');
  }

  // Hands back to the role, idle pool back to the civ. A card that raises
  // hands gives them to the role the Hall named.
  // A warband standing in a town another people took wins it back.
  if (role === 'military') {
    for (const u of unitsOf(civ)) {
      const t = takenTownAt(civ, u.q, u.r);
      if (!t || u.kind !== 'warband') continue;
      civ = {
        ...civ,
        villages: (civ.villages || []).map((v) => (v.id === t.id ? { ...v, taken: null } : v)),
        rivals: rivalsOf(civ).map((r) => (r.id === t.taken ? { ...r, held: (r.held || []).filter((k) => k !== `${t.q},${t.r}`), stance: 'hostile' } : r)),
      };
      lines.push(`${t.name} is ours again`);
    }
  }
  // A hired guide is one more order in hand today.
  if (outcome.hire) { civ = { ...civ, workers: civ.workers + outcome.hire }; lines.push(`a guide is hired: +${outcome.hire} order today`); }
  // Everything this action moved, as a patch the phone can draw in pips.
  const gained = changeBetween(startedWith, countsOf(civ));
  const hands = { ...(next.hands || emptyByRole(0)), [role]: civ.workers };
  const reopened = [];
  // A card that raises workers raises WORKERS: the Stewards turn theirs
  // into half-days like any others, which dawn does and this used not to.
  if (handsGiven) {
    (Array.isArray(handsGiven) ? handsGiven : [handsGiven]).forEach(({ to, n }) => {
      if (to === 'culture' && hallOf(civ)) {
        // The Hall's workers are its cards (2026-10-14), mid-day too: a Levy
        // handed to the Hall is dealt straight into its hand (playtest 11,
        // Nick: "the Hall doesn't do that... can't do anything with workers").
        const dealt = dealFor(n);
        let drawn;
        let { rng } = next;
        [drawn, rng] = drawCards(hallOf(civ), dealt, rng);
        next = { ...next, rng };
        civ = { ...civ, hall: drawn };
        lines.push(`the Hall is dealt ${dealt} ${dealt === 1 ? 'card' : 'cards'} for them`);
        return;
      }
      hands[to] = (hands[to] || 0) + shiftsFor(to, n);
      // A role that had pressed Done is open again: it has something to do
      // now (playtest 13: a levy could only go to whoever was still up, and
      // the workaround was "undo your done").
      if ((next.done || {})[to]) { reopened.push(to); next = { ...next, done: { ...next.done, [to]: false } }; }
    });
  }
  if (reopened.length) lines.push(`${reopened.map((r) => `the ${ROLE_INFO[r].label}`).join(' and ')} ${reopened.length === 1 ? 'is' : 'are'} back up for the day`);
  civ = { ...civ, workers: idle };
  const taken = { ...(next.taken || emptyByRole(null)), [role]: { ...((next.taken || {})[role] || {}), [actionId]: (((next.taken || {})[role] || {})[actionId] || 0) + 1 } };
  next = { ...next, civ, hands, taken };
  // What this act showed that cannot be un-seen, if anything.
  const drew = (hallOf(civ)?.hand || []).filter((id, i, arr) => (hallOf(state.civ)?.hand || []).filter((x) => x === id).length < arr.filter((x) => x === id).length).length > 0;
  const blocked = rolls ? 'a die was rolled'
    : explored ? 'new land rose'
      : (civ.seen || []).length > (state.civ.seen || []).length ? 'new ground came into sight'
        : (civ.finds || []).length < (state.civ.finds || []).length ? 'something was found'
          : actionId === 'mix' ? 'the glass has spoken'
            : actionId === 'fire' ? 'the machine has spoken'
            : actionId === 'hint' ? 'the stones have spoken'
              : drew ? 'cards were drawn'
                : handsGiven && [].concat(handsGiven).some((g) => g.to !== role) ? `workers went to the ${[].concat(handsGiven).filter((g) => g.to !== role).map((g) => ROLE_INFO[g.to]?.label || g.to).join(' and ')}`
                  : null;
  // Only what this act changed is kept: unwind skips anything equal in both
  // anyway, and full copies of the state made a long game's sync heavy
  // (two ~20KB snapshots an action, up to UNDO_DEPTH a role).
  const [slimBefore, slimAfter] = slimPair(before, snapshot(next, role));
  const entry = {
    seq: state.meta.seq, actionId, label, blocked, before: slimBefore, after: slimAfter,
  };
  next = { ...next, undoStack: { ...(next.undoStack || {}), [role]: [...undoStackOf(next, role), entry].slice(-UNDO_DEPTH) } };
  const who = next.players.find((p) => p.id === next.roles[role])?.name;
  const roleName = ROLE_INFO[role]?.label || role;
  // "I thought the monument was going to cost us two coins... now we're down
  // to zero": the line says what was paid as well as what was gained.
  const paid = describeCost(cost);
  next = logLine(next, {
    kind: 'act',
    role,
    action: actionId,
    ...(rolls ? { rolls } : {}),
    ...(sides ? { sides } : {}),
    ...(explored || {}),
    ...(stage ? { stage: { ...stage, ...(stage.battle ? {} : { at: (() => { const u = unitById(civ, stage.unit); return u ? [u.q, u.r] : stage.at; })() }) } } : {}),
    // The same story in parts, so a board can draw it rather than print it.
    label,
    paid: { ...cost },
    gained,
    ...(role === 'military' ? { notices: frontierNotices(state.civ, civ) } : {}),
    ...revealsEntry(state.civ, civ, `${roleName} - ${label}`),
    said: withTotals(civ, lines).join(', '),
    text: `${who ? `${who} as ${roleName}` : roleName} - ${label}: ${paid ? `paid ${paid}; ` : ''}${withTotals(civ, lines).join(', ')}.`,
  });

  next = checkGoal(next);
  if (next.meta.phase === PHASES.FINISHED) return bump(next);

  return bump(checkCollapse(next));
};

/** The round's event: one card off the top, face up on the table. */
const flipEvent = (state) => {
  const { round } = state.meta;
  // THE FIXED MONTHS turn their own card, whatever is in the deck: January
  // the year ahead, September the Harvest, December Midwinter. A deck that
  // has somehow run dry mid-year is topped up from the season. The first
  // January is the founding itself - the year's purpose is the Vision in
  // the top band - so the year card starts in year 2.
  let cardId;
  let deck = state.deck;
  let state1 = state;
  if (isYearCard(round) && round > MONTHS_PER_YEAR) cardId = 'year_card';
  else if (isHarvest(round)) cardId = 'the_harvest';
  else if (isMidwinter(round)) cardId = 'midwinter';
  // THE PEOPLE (engine/demands.js): a chosen demand asks on its hidden month,
  // instead of a deck card, so nothing seeded later can bury it; with none
  // pending and the town past four, they speak up again after a gap.
  else if (state.civ.demand && state.civ.demand.due <= round) cardId = 'the_people_ask';
  // Never while the Vision still waits: the year's purpose comes first.
  else if (!state.civ.demand && demandsAwake(state.civ) && round >= (state.civ.demandNext ?? 0) && !state.deck.includes(GOAL_CARD)) cardId = 'the_people_speak';
  else {
    if (!deck.length) {
      let more;
      let { rng } = state1;
      const pool = seasonPool(seasonOf(round), state1.removed, state1.civ.age || 0);
      [more, rng] = shuffle(pool.length ? pool : BASE_POOL.filter((id) => !state1.removed.includes(id)), rng);
      deck = more.slice(0, 1);
      state1 = { ...state1, rng };
    }
    [cardId, ...deck] = deck;
  }
  state = state1;
  const card = EVENTS[cardId];
  let next = { ...state, deck, meta: { ...state.meta, phase: PHASES.EVENT } };
  let event = { cardId, lines: [], resolved: false, choice: null, branch: null };

  if (card.reveal === 'yearGoal') {
    // The year ahead: one of two goals small enough for a sitting. The
    // town's numbers now are the mark the goal is measured against.
    let offer;
    let { rng } = next;
    [offer, rng] = shuffle(yearGoalsOpen(next.civ), rng);
    offer = offer.slice(0, YEAR_GOALS_OFFERED);
    next = { ...next, rng, civ: { ...next.civ, yearStart: yearSnapshot(next.civ), yearGoal: null } };
    if (!offer.length) {
      next = { ...next, discard: [...next.discard, cardId] };
      next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. Year ${yearOf(round)} begins; every victory is won.` });
      return { ...next, event: { ...event, resolved: true } };
    }
    event.choices = offer.map((id) => ({ id, label: YEAR_GOALS[id].name, text: YEAR_GOALS[id].text, yearGoal: id }));
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. Year ${yearOf(round)} begins. ${offer.map((id) => YEAR_GOALS[id].name).join(' or ')}?` });
    return { ...next, event };
  }
  if (card.reveal === 'demandOffer' && !next.civ.demand) {
    let offer;
    let { rng } = next;
    [offer, rng] = offerDemands(next.civ, rng);
    next = { ...next, rng };
    if (!offer.length) {
      next = { ...next, civ: { ...next.civ, demandNext: round + demandGap(next.civ) } };
      next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. The people have no demand this time.` });
      return { ...next, event: { ...event, resolved: true } };
    }
    event.choices = offer.map((d, i) => ({ id: `d${i}`, label: d.label, text: `${d.text} Met when they ask: +${demandReward(d).happiness} happiness, +${demandReward(d).coin} coin. Missed: ${demandPenalty().happiness} happiness and a grievance.`, demand: d }));
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${offer.map((d) => d.label).join(' or ')}?` });
    return { ...next, event };
  }
  if (card.reveal === 'demandAsk' && next.civ.demand) {
    let civ = next.civ;
    const d = civ.demand;
    let lines = [];
    if (demandMet(civ, d)) {
      [civ, lines] = applyPatch(civ, demandReward(d));
      lines = [`they asked for what was promised (${d.label}), and it is there`, ...lines];
    } else {
      [civ, lines] = applyPatch(civ, demandPenalty(d));
      civ = grieve({ ...civ, grieved: true }, 'grievance');
      lines = [`they asked for what was promised (${d.label}), and it is not there`, ...lines, 'a grievance goes into the Hall\'s deck'];
    }
    civ = { ...civ, demand: null, demandNext: round + demandGap(civ) };
    next = { ...next, civ, discard: [...next.discard, cardId] };
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${withTotals(civ, lines).join(', ')}.` });
    return checkCollapse({ ...next, event: { ...event, lines, resolved: true } });
  }
  if (card.reveal === 'tale') {
    // Midwinter: the feast, then the tale. The year's goal is judged first,
    // so its reward is on the table before the choice.
    let civ = next.civ;
    let lines = [];
    [civ, lines] = applyPatch(civ, card.effect || {});
    const goal = civ.yearGoal && YEAR_GOALS[civ.yearGoal.id];
    if (goal) {
      if (goal.done(civ, civ.yearGoal.mark)) {
        let got;
        [civ, got] = applyPatch(civ, goal.reward);
        civ = { ...civ, yearGoal: { ...civ.yearGoal, met: true } };
        lines = [...lines, `the year's goal is met - ${goal.name}: ${goal.rewardText}`, ...got.filter((l) => !/happiness/.test(l) || true).slice(0, 0)];
      } else {
        civ = { ...civ, yearGoal: { ...civ.yearGoal, met: false } };
        lines = [...lines, `the year's goal was not met - ${goal.name}`];
      }
    }
    let offer;
    let { rng } = next;
    [offer, rng] = shuffle(talesOpen(civ), rng);
    offer = offer.slice(0, TALES_OFFERED);
    next = { ...next, rng, civ };
    event.lines = lines;
    if (!offer.length) {
      next = { ...next, discard: [...next.discard, cardId] };
      next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${withTotals(civ, lines).join(', ')}.` });
      return { ...next, event: { ...event, resolved: true } };
    }
    event.choices = offer.map((id) => ({ id, label: TALES[id].name, text: `${TALES[id].text} Gift: ${TALES[id].gift}. Cost: ${TALES[id].cost}.`, tale: id }));
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${withTotals(civ, lines).join(', ')}. Choose a tale to keep.` });
    return { ...next, event };
  }
  if (card.harvest) {
    // September: the fields and the villages bring in a lump on top of the
    // night's usual take, and the granary's overflow keeps ONE night - the
    // table has tomorrow to spend it, send it or build with it.
    let civ = next.civ;
    const lump = {};
    villagesOf(civ).filter((v) => !v.damaged).forEach((v) => {
      Object.entries(villageYield(civ, v)).forEach(([k, n]) => { lump[k] = (lump[k] || 0) + n * HARVEST_TIMES; });
    });
    lump.food = (lump.food || 0) + (civ.farms || 0) * fieldFood(civ);
    let got;
    [civ, got] = applyPatch(civ, lump);
    civ = { ...civ, harvestNight: round };
    event.lines = got;
    event.resolved = true;
    next = { ...next, civ, event, discard: [...next.discard, cardId] };
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${withTotals(civ, got).join(', ') || 'a thin harvest - nothing came in'}.`, notices: frontierNotices(state.civ, civ), ...revealsEntry(state.civ, civ, card.name) });
    return checkCollapse(next);
  }
  if (card.reveal === 'goal') {
    // The vision: two wonders, and the group must pick one. The choices are
    // built from the state, so they live on the event, not the card.
    const offer = (next.civ.goalOffer || GOAL_ORDER.slice(0, GOALS_OFFERED)).filter((id) => GOALS[id] && !wondersOf(next.civ).includes(id) && !(next.civ.lostWonders || []).includes(id));
    if (!offer.length) {
      next = { ...next, discard: [...next.discard, cardId] };
      next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. Every wonder stands already.` });
      return { ...next, event: { ...event, resolved: true } };
    }
    event.choices = offer.map((id) => ({ id, label: GOALS[id].name, text: GOALS[id].text, goal: id }));
    next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${offer.map((id) => GOALS[id].name).join(' or ')}?` });
    return { ...next, event };
  }
  if (card.choices) {
    // Somebody has to decide. The round parks here. A card about a
    // neighbouring people says which one.
    // Which people: the oldest of their cards still waiting (rivalCards),
    // so two peoples' cards in the deck never swap names.
    let who = null;
    if (card.rival) {
      const queue = next.civ.rivalCards || [];
      const at = queue.findIndex((c) => c.card === cardId);
      who = rivalById(next.civ, at >= 0 ? queue[at].rival : next.civ.rivalMeeting);
      if (at >= 0) next = { ...next, civ: { ...next.civ, rivalMeeting: queue[at].rival, rivalCards: queue.filter((_, i) => i !== at) } };
    }
    const name = who ? `${card.name} - ${who.name}` : card.name;
    if (who) event = { ...event, rival: who.id, rivalName: who.name };
    // Envoys to a people who are ALREADY friends had nothing to offer but
    // "pay to become friendly" (playtest 11, Matt: "the envoy from the Salt
    // Clans and the Iron Suns gave us exactly the same pointless decision").
    // Friends come to trade instead.
    if (who && cardId === 'rival_envoys' && who.stance === 'friendly') {
      event = { ...event, choices: EVENTS.rival_offer.choices };
      next = logLine(next, { kind: 'event', card: cardId, rival: who.id, text: `The world turns: ${name}. They are friends already, so they come with carts to trade.` });
      return { ...next, event };
    }
    next = logLine(next, { kind: 'event', card: cardId, ...(who ? { rival: who.id } : {}), text: `The world turns: ${name}. ${card.text}` });
    return { ...next, event };
  }
  let civ = next.civ;
  if (card.spawn) {
    // The threat, made visible: something appears on the map.
    let said;
    [next, said] = spawnMenace(next, card.spawn);
    civ = next.civ;
    event.lines = [said];
  } else if (card.odds) {
    // The tracks are odds: a d10 under the chance fires the card.
    const chance = Math.max(0, Math.min(DIE, card.odds.chance(civ)));
    let face;
    let { rng } = next;
    [face, rng] = nextInt(DIE, rng);
    next = { ...next, rng };
    const rolled = face + 1;
    const fired = rolled <= chance;
    event.roll = { rolled, chance, against: card.odds.against, fired };
    event.branch = `Rolled ${rolled} against ${card.odds.against} (${chance} in ${DIE}). ${fired ? card.odds.thenText : card.odds.elseText}`;
    if (fired) [civ, event.lines] = applyPatch(civ, card.odds.then);
  } else if (card.effect) {
    [civ, event.lines] = applyPatch(civ, card.effect);
  }
  event.resolved = true;
  if (card.compass) { civ = pushCompass(civ, card.compass); }
  next = { ...next, civ, event, discard: [...next.discard, cardId] };
  const extras = [];
  if (card.compass) extras.push(`the compass moves ${describePush(card.compass)}`);
  // An effect card can still carry the story on, or open a branch.
  [].concat(card.adds || []).forEach((id) => { next = seedCard(next, id); });
  if (card.adds) extras.push([].concat(card.adds).length === 1 ? 'a card was added to the deck' : `${[].concat(card.adds).length} cards were added to the deck`);
  if (card.unlocks) {
    let opened;
    [next, opened] = openBranch(next, card.unlocks, card.name);
    if (opened) extras.push(opened);
  }
  if (card.reveals) {
    let said;
    let c2;
    [c2, said] = revealAspect(next.civ, card.reveals[0], card.reveals[1]);
    next = { ...next, civ: c2 };
    if (said) extras.push(`the Scholars learn: ${said}`);
  }
  civ = next.civ;
  // A card can land on nothing (a drought when the stores are already
  // empty). Say so, rather than logging "Drought: ".
  const said = [event.branch, [withTotals(civ, event.lines).join(', '), ...extras].filter(Boolean).join(', ')].filter(Boolean).join(' ') || 'nothing changes';
  next = logLine(next, { kind: 'event', card: cardId, text: `The world turns: ${card.name}. ${card.text} ${said}.`, notices: frontierNotices(state.civ, civ), ...revealsEntry(state.civ, civ, card.name) });
  return checkCollapse(next);
};

const applyResolveEvent = (state, { choice }) => {
  if (state.meta.phase !== PHASES.EVENT || !state.event || state.event.resolved) return state;
  const card = EVENTS[state.event.cardId];
  const option = (state.event.choices || card.choices || []).find((c) => c.id === choice);
  if (!option) return state;
  if (option.requires && !canAfford(state.civ, option.requires)) return state;
  let [civ, lines] = applyPatch(state.civ, option.effect || {});
  if (option.extraWorkers) {
    civ = { ...civ, extraWorkers: (civ.extraWorkers || 0) + option.extraWorkers };
    lines = [...lines, `${option.extraWorkers} workers at the next dawn`];
  }
  if (option.demand) {
    // Due on a hidden month in the window, never a fixed month (those turn
    // their own cards); the screens say only how many months are left.
    const round = state.meta.round;
    const fixed = (r) => (isYearCard(r) && r > MONTHS_PER_YEAR) || isHarvest(r) || isMidwinter(r);
    let months = [];
    for (let r = round + 1; months.length < DEMANDS.window && r < round + DEMANDS.window + 4; r += 1) if (!fixed(r)) months.push(r);
    let at;
    let rng0 = state.rng;
    [at, rng0] = nextInt(months.length, rng0);
    civ = { ...civ, demand: { ...option.demand, chosen: round, due: months[at], windowEnd: months[months.length - 1] } };
    state = { ...state, rng: rng0 };
    // Counted from next month: this one is over by the time anyone reads it.
    lines = [`promised to the people: ${option.demand.label}. They will ask ${demandWhen(civ.demand, round + 1)}`];
  }
  if (option.yearGoal && YEAR_GOALS[option.yearGoal]) {
    const g = YEAR_GOALS[option.yearGoal];
    civ = { ...civ, yearGoal: { id: option.yearGoal, mark: g.mark(civ), year: yearOf(state.meta.round) } };
    lines = [`this year: ${g.name} - ${g.text}`];
  }
  if (option.tale && TALES[option.tale]) {
    const t = TALES[option.tale];
    civ = { ...civ, tales: [...talesOf(civ), option.tale], taleYear: yearOf(state.meta.round) };
    let now = [];
    if (t.now) [civ, now] = applyPatch(civ, t.now);
    lines = [`the town will carry ${t.name}: ${t.gift}; ${t.cost}`, ...now];
  }
  if (option.goal && !wondersOf(civ).includes(option.goal)) {
    // A great work to raise. If one is already half-built, a story's wonder
    // waits its turn at the front of the next offer rather than throwing
    // the stages away.
    const was = goalOf(civ);
    if (was && (civ.monument?.stages || 0) > 0 && was !== option.goal) {
      civ = { ...civ, goalOffer: [option.goal, ...(civ.goalOffer || []).filter((g) => g !== option.goal)] };
      lines = [`${GOALS[option.goal].name} will be offered once ${GOALS[was].name} stands`];
    } else {
      civ = { ...civ, goal: option.goal, monument: { revealed: true, stages: 0 } };
      lines = [was && was !== option.goal ? `the great work is now ${GOALS[option.goal].name} (it was ${GOALS[was].name})` : `the great work is ${GOALS[option.goal].name}`];
    }
  }
  // The neighbours: a gift warms them, a door in the face makes them hostile.
  const rivalId = state.event.rival || civ.rivalMeeting;
  if (option.stance && rivalId && rivalById(civ, rivalId)) {
    civ = { ...civ, rivals: rivalsOf(civ).map((r) => (r.id === rivalId ? { ...r, stance: option.stance } : r)) };
    lines = [...lines, `${rivalById(civ, rivalId).name} are ${option.stance} now`];
  }
  if (option.warparty && rivalId && rivalById(civ, rivalId)) {
    const r = rivalById(civ, rivalId);
    const from = raidFrom(civ, r);
    if (from) {
      const seq = (civ.raiderSeq || 0) + 1;
      civ = {
        ...civ,
        raiders: [...raidersOf(civ), { id: `x${seq}`, from: r.id, name: `${r.name}'s war party`, q: from.q, r: from.r, strength: 2 + r.level }],
        raiderSeq: seq,
      };
      lines = [...lines, `a war party of ${r.name} sets out, strength ${2 + r.level}`];
    }
  }
  let next = {
    ...state,
    civ,
    event: { ...state.event, resolved: true, choice, lines },
    discard: [...state.discard, state.event.cardId],
  };
  // Storylines: a choice can put the next chapter into the deck, sight
  // unseen, or take a chapter out. Matt: "based on the choices they make,
  // further events can be added into the deck, so you end up with sort of
  // storylines as you go."
  const said = [withTotals(civ, lines).join(', ')].filter(Boolean);
  const adds = [].concat(option.adds || []);
  adds.forEach((id) => { next = seedCard(next, id); });
  if (adds.length) said.push(adds.length === 1 ? 'a card was added to the deck' : `${adds.length} cards were added to the deck`);
  if (option.removes) {
    const before = next.deck.length;
    next = removeCards(next, [].concat(option.removes));
    const n = before - next.deck.length;
    if (n) said.push(`${n} ${n === 1 ? 'card' : 'cards'} removed from the deck`);
  }
  if (option.unlocks) {
    let opened;
    [next, opened] = openBranch(next, option.unlocks, option.label);
    if (opened) said.push(opened);
  }
  if (option.grieve) {
    next = { ...next, civ: grieve(next.civ, option.grieve) };
    said.push(`a ${option.grieve} goes into the Hall's deck`);
  }
  if (option.stays) next = { ...next, civ: { ...next.civ, strangerStayed: true } };
  if (option.compass) {
    next = { ...next, civ: pushCompass(next.civ, option.compass) };
    said.push(`the compass moves ${describePush(option.compass)}`);
  }
  next = logLine(next, { kind: 'choice', text: `${option.label}: ${said.join(', ') || 'nothing changes'}.`, notices: frontierNotices(state.civ, next.civ), ...revealsEntry(state.civ, next.civ, option.label) });
  return bump(checkCollapse(next));
};

/**
 * Continue: from the tally, the card turns; from a resolved card, the
 * night falls. Both are everyone's to press.
 */
// THE NIGHT, AS CARDS, ALWAYS IN THIS ORDER (Matt, after playtest 12: "a
// precise and consistent order to the things that happen at night").
export const NIGHT_SECTIONS = [
  ['food', 'Supper'],
  ['mood', 'The town\'s mood'],
  ['people', 'Who came, who grew'],
  ['income', 'What came in'],
  ['stores', 'The stores'],
  ['raids', 'Raiders'],
  ['peoples', 'The other peoples'],
  ['hall', 'The Hall'],
];
const nightCards = (beats, round) => NIGHT_SECTIONS
  .map(([id, title]) => ({ id, title, lines: beats.filter((b) => b.sec === id).map((b) => b.text.charAt(0).toUpperCase() + b.text.slice(1)) }))
  .filter((c) => c.lines.length)
  .map((c) => ({ ...c, round }));
/** Anyone moves the night on a card; every screen follows. */
const applyNightNext = (state, { round } = {}) => {
  const read = state.nightRead;
  if (!read || (round !== undefined && read.round !== round)) return state;
  return bump({ ...state, nightRead: { ...read, at: read.at + 1 } });
};

const applyContinue = (state) => {
  if (state.meta.phase === PHASES.TALLY) return bump(flipEvent(state));
  if (state.meta.phase === YEAR_END) return applyNewYear(state);
  if (state.meta.phase !== PHASES.EVENT || !state.event?.resolved) return state;
  let civ = state.civ;
  const lines = [];
  // Each line is one beat of the night on the table, so they are written
  // to be read aloud, not just tallied. And each belongs to a SECTION, so
  // the night can be read as cards in one fixed order (NIGHT_SECTIONS):
  // set `sec` before a block, and every line pushed after it is filed there.
  let sec = 'raids';
  const beats = [];
  const record = lines.push.bind(lines);
  lines.push = (...said) => { said.forEach((text) => beats.push({ sec, text })); return record(...said); };

  // The network's yield arrives at DAWN now, not here (playtest 11, Matt:
  // "it should give you one each dawn, so we already have it and can count
  // on it; we don't have to guess if it's gonna come"): see applyStartDay.
  sec = 'raids';
  const damaged = villagesOf(civ).filter((v) => v.damaged).length;
  if (damaged) lines.push(`${damaged} ${damaged === 1 ? 'settlement is' : 'settlements are'} wrecked and sending nothing`);

  // A settlement left in peace grows on its own. A caravan is the fast way
  // to feed one; this is the patient way, and it is what makes the village
  // -> town ladder reachable in an era rather than a lucky accident.
  sec = 'people';
  const grown = [];
  civ = {
    ...civ,
    villages: villagesOf(civ).map((v) => {
      const age = (v.age || 0) + 1;
      if (v.damaged || v.size >= (v.city ? CITY.maxSize : (v.chartered ? TOWN.maxSize : VILLAGE.maxSize)) || age % VILLAGE.growsEvery !== 0) return { ...v, age };
      grown.push(v.name);
      return { ...v, age, size: v.size + 1 };
    }),
  };
  if (grown.length) lines.push(`${grown.join(' and ')} ${grown.length === 1 ? 'grows' : 'grow'}`);

  // And a TOWN eats, which is what stops chartering from being strictly
  // better. A village is families sending up a surplus; a town is mouths.
  sec = 'food';
  const towns = villagesOf(civ).filter((v) => v.chartered).length;
  const cities = villagesOf(civ).filter((v) => v.city).length;
  const eaten = Math.ceil(civ.population * FOOD_PER_PERSON) + (towns - cities) * TOWN.upkeepFood + cities * CITY.upkeepFood;
  if (civ.food >= eaten) {
    civ = { ...civ, food: civ.food - eaten };
    lines.push(towns
      ? `the people and ${towns} ${towns === 1 ? 'town' : 'towns'} ate ${eaten} food (${civ.food} left)`
      : `the people ate ${eaten} food (${civ.food} left)`);
  } else {
    let famine;
    const hurt = Math.max(0, FAMINE_HAPPINESS + modsOf(civ).famineHappiness);
    // Say the sum (playtest 12: a family arrived and starved the same night,
    // and "it would've needed another food" was a guess).
    const mouths = `${civ.population} ${civ.population === 1 ? 'person' : 'people'}${towns ? ` and ${towns} ${towns === 1 ? 'town' : 'towns'}` : ''} needed ${eaten} food and there ${civ.food === 1 ? 'was' : 'were'} ${civ.food}`;
    [civ, famine] = applyPatch({ ...civ, food: 0 }, { happiness: -hurt, population: -FAMINE_POPULATION });
    // The Hall carries it: a grievance in the deck, and the town turns to the old ways.
    civ = pushCompass(grieve({ ...civ, grieved: true }, 'grievance'), { x: -1 });
    lines.push(`famine: ${mouths}; ${withTotals(civ, famine).join(', ')}`, 'a grievance goes into the Hall\'s deck');
  }
  // A GLAD, FED TOWN GROWS. A family arrives on a die roll when the larder
  // and the mood allow - the second lever on population, and something for
  // happiness above the line to DO (2026-09-21).
  sec = 'people';
  let rngB = state.rng;
  const quietYear = modsOf(civ).birthsNeedJoy > 0 && (civ.yearStart?.happiness ?? 10) < 7;
  if (!quietYear && civ.population < housingOf(civ) && civ.happiness >= BIRTH.happiness && civ.food >= BIRTH.food) {
    let face;
    [face, rngB] = nextInt(6, rngB);
    const need = Math.max(2, BIRTH.on - modsOf(civ).births);
    if (face + 1 >= need) {
      // The table decides what to do with them: the card turns next.
      // Under the first Vision if it is still waiting, so the goal is never buried.
      const deck0 = state.deck.filter((id) => id !== 'a_family');
      const goalAt = deck0.indexOf(GOAL_CARD);
      deck0.splice(goalAt >= 0 ? goalAt + 1 : 0, 0, 'a_family');
      state = { ...state, deck: deck0 };
      lines.push(`a family is at the gate (a fed town at 8+ happiness rolls for one each night: needed ${need}+, rolled ${face + 1}) - the next card asks what to do with them`);
    } else lines.push(`no family arrived tonight (a fed town at 8+ happiness rolls for one each night: needed ${need}+, rolled ${face + 1})`);
  } else if (!quietYear && civ.population >= housingOf(civ) && civ.happiness >= BIRTH.happiness && civ.food >= BIRTH.food) {
    // Glad and fed, and nowhere to put anyone: say so, or the town just
    // stops growing for no reason anyone can see (playtest 11, the cap at 12).
    lines.push(`the town is full: ${housingOf(civ)} homes, ${civ.population} people - the Stewards can build a House for more`);
  }
  state = { ...state, rng: rngB };
  // AND THE GLOW FADES. Happiness above the line loses one every night, so
  // a glad town is the Hall's standing work, not a thing done once.
  sec = 'mood';
  // How glad the town went to bed, for the glad worker at dawn: judged
  // AFTER the fade it could never happen - 10 fades to 8, under the 9 it
  // needs (playtest 2026-10-20: "we can't get the extra person no matter what").
  const duskHappiness = civ.happiness;
  if (civ.happiness > HAPPINESS_FADE_ABOVE) {
    let faded;
    // The winter tales can make the glow keep better or worse.
    const fade = Math.max(0, (civ.happiness > HAPPINESS_FADE_FAST_ABOVE ? 2 : 1) + modsOf(civ).fadeMore - modsOf(civ).fadeLess);
    [civ, faded] = applyPatch(civ, { happiness: -fade });
    if (faded.length) lines.push(`happiness over ${HAPPINESS_FADE_ABOVE} drifts back down every night: ${withTotals(civ, faded).join(', ')}`);
  }
  // (The nightly wants are retired: the people's DEMANDS replace them -
  // engine/demands.js, Matt after playtest 12.)
  sec = 'income';
  if (civ.ideasDue) {
    let got;
    // Name what sparked it (playtest 12, Matt: "every night it says born of
    // an older idea... what the hell does that mean").
    const from = (civ.ideasFrom || []).map((id) => TECHS[id]?.name).filter(Boolean);
    [civ, got] = applyPatch({ ...civ, ideasDue: 0, ideasFrom: [] }, { knowledge: EVENTS.eureka.effect.knowledge * civ.ideasDue });
    if (got.length) lines.push(`${from.length ? `learning ${from.join(' and ')} sparks` : 'a new technology sparks'} ${civ.ideasDue === 1 ? 'an idea' : 'ideas'} overnight: ${withTotals(civ, got).join(', ')}`);
  }
  const nightly = { knowledge: modsOf(civ).nightKnowledge, goods: modsOf(civ).goodsNight, food: modsOf(civ).foodNight };
  if (Object.values(nightly).some(Boolean)) {
    let got;
    [civ, got] = applyPatch(civ, nightly);
    if (got.length) lines.push(`the night brings ${withTotals(civ, got).join(', ')}`);
  }
  sec = 'skip';
  const unused = civ.workers + ROLES.reduce((n, r) => n + ((state.hands || {})[r] || 0), 0);
  if (unused > 0) lines.push(`${unused} unused workers go home`);
  civ = { ...civ, workers: 0 };
  // ------------------------------------------------ the world out there
  // A CAMP DOES NOT GET STRONGER, IT GETS BUSIER. Each night it rolls a die
  // and sends a raider on a roll at or under its age in rounds. The raiders
  // then walk - at a village if one is nearer, at the city otherwise - and
  // what they do when they arrive is the whole of the threat now. See
  // engine/units.js and docs/DESIGN.md.
  sec = 'raids';
  let rng = state.rng;
  let raiders = [...raidersOf(civ)];
  let seq = civ.raiderSeq || 0;
  // The Lighthouse and Diplomacy each make a camp idle one night in two; both
  // together, two in three (they stack since playtest 10's audit).
  const slow = Math.max(0, modsOf(civ).menaceSlow);

  for (const m of civ.menaces || []) {
    if (slow && m.age % (slow + 1) !== 1) continue;
    // At most CAMP.cap of a camp's raiders abroad, one every CAMP.sendEvery
    // nights it is able to. The Wolf Winter tale makes the dens twice as busy.
    const abroad = raidersOf(civ).filter((x) => x.from === m.id).length;
    let sends = sendsTonight(m, 0, abroad);
    if (!sends && m.kind === 'den' && modsOf(civ).wolvesMore && (m.age || 0) > 0 && abroad < CAMP.cap && m.age % Math.max(1, Math.floor(CAMP.sendEvery / 2)) === 0) sends = true;
    if (!sends) continue;
    const where = spawnHexes(civ, m);
    if (!where.length) continue;
    let at;
    [at, rng] = nextInt(where.length, rng);
    const kind = raiderFor(m);
    seq += 1;
    const born = {
      id: `x${seq}`, from: m.id, name: kind.name, q: where[at][0], r: where[at][1], strength: kind.strength + THREAT_SHAPE.ageStrength[civ.age || 0],
    };
    raiders = [...raiders, born];
    civ = { ...civ, raiders, raiderSeq: seq };
    lines.push(`${kind.name.toLowerCase()} come out of the ${m.kind} at ${placeName(civ, born.q, born.r)}`);
  }
  civ = { ...civ, menaces: (civ.menaces || []).map((m) => ({ ...m, age: (m.age || 0) + 1 })), raiders, raiderSeq: seq };
  // AND THE FOG BREEDS CAMPS (see CAMP_RETURN): quiet early in the year,
  // then a new one every few months while there is room, a little stronger
  // each year. Clearing them is the Frontier's standing work.
  {
    const { round } = state.meta;
    const month = monthOf(round);
    const year = yearOf(round);
    const rule = CAMP_RETURN.byYear[Math.min(year, CAMP_RETURN.byYear.length) - 1];
    const camps = (civ.menaces || []).filter((m) => m.kind === 'camp').length;
    if (month >= rule.from && month <= CAMP_RETURN.lastMonth && (month - rule.from) % rule.every === 0 && camps < rule.maxOut) {
      let st;
      let said;
      [st, said] = spawnMenace({ ...state, rng, civ }, { kind: 'camp', strength: campStrengthIn(year, RAIDER_KINDS.camp.strength) });
      civ = st.civ;
      rng = st.rng;
      lines.push(said);
    }
  }

  // INVADERS FROM THE SEA (the Classical Age): a war party lands on a coast
  // at the edge of the known and walks inland (THREAT_SHAPE).
  if ((civ.age || 0) >= THREAT_SHAPE.invasionAge && state.meta.round % THREAT_SHAPE.invasionEvery === 0) {
    const seed = civ.mapSeed || 0;
    const coast = campSpots(civ).filter(([q, r]) => neighbours(q, r).some(([a, b]) => !isLandHex(a, b, seed)) && !raidersOf(civ).some((x) => x.q === q && x.r === r));
    if (coast.length) {
      let ci;
      [ci, rng] = nextInt(Math.min(6, coast.length), rng);
      seq += 1;
      const [q, r] = coast[ci];
      civ = { ...civ, raiders: [...raidersOf(civ), { id: `x${seq}`, from: 'sea', name: 'Invaders', q, r, strength: THREAT_SHAPE.invasionStrength }], raiderSeq: seq };
      raiders = raidersOf(civ);
      lines.push(`invaders land from the sea at ${placeName(civ, q, r)}, strength ${THREAT_SHAPE.invasionStrength}`);
    }
  }

  // THE OTHER PEOPLES have their night: they grow, they take ground, and
  // they do what their temper says. See engine/rivals.js.
  sec = 'peoples';
  let cards = [];
  for (const r0 of rivalsOf(civ)) {
    let r = { ...r0, age: (r0.age || 0) + 1 };
    const way = wayOf(r);
    if (r.age % way.growEvery === 0 && r.power < RIVAL.maxPower) r = { ...r, power: r.power + 1 };
    if (r.age % RIVAL.levelEvery === 0) r = { ...r, level: r.level + 1 };
    if (r.age % way.expandEvery === 0 && (r.held || []).length < way.maxHeld) {
      const claim = nextClaim(civ, r);
      if (claim) {
        r = { ...r, held: [...r.held, hexKey(claim.q, claim.r)] };
        const found = findAt(civ, claim.q, claim.r);
        if (found) {
          civ = { ...civ, finds: (civ.finds || []).filter((f) => f.id !== found.id), findsTaken: [...(civ.findsTaken || []), hexKey(found.q, found.r)] };
          lines.push(`${r.name} take ${placeName(civ, claim.q, claim.r)} - and ${FINDS[found.kind].name.toLowerCase()} with it`);
        } else if ((civ.seen || []).includes(hexKey(claim.q, claim.r))) lines.push(`${r.name} take ${placeName(civ, claim.q, claim.r)}`);
      }
    }
    // Meeting: the first time their ground is in our sight, envoys come.
    if (!r.met && inSight({ ...civ, rivals: rivalsOf(civ).map((x) => (x.id === r.id ? r : x)) }, r)) {
      r = { ...r, met: true };
      cards.push({ card: 'rival_envoys', rival: r.id });
      lines.push(`the fires of ${r.name} are in sight - envoys will come`);
    } else if (!r.met && r.age >= RIVAL.findsYouAt) {
      // A people nobody went looking for comes looking (after playtest 9:
      // the Iron Sons sat unmet all year and might as well not have been
      // there). Their riders find the town; their land goes on the map.
      r = { ...r, met: true };
      civ = { ...civ, seen: [...new Set([...(civ.seen || []), ...(r.held || [])])] };
      cards.push({ card: 'rival_envoys', rival: r.id });
      lines.push(`riders of ${r.name} find the town - their land is marked on the map, and envoys will come`);
    }
    if (r.met) {
      if (r.stance === 'hostile' && !r.allied) {
        let face;
        [face, rng] = nextInt(6, rng);
        if (face + 1 <= Math.min(RIVAL.raidUnder + ((civ.age || 0) >= 1 ? THREAT_SHAPE.rivalRaidsMore : 0), Math.floor(r.age / 3))) {
          const from = raidFrom(civ, r);
          if (from) {
            seq += 1;
            raiders = [...raidersOf(civ), { id: `x${seq}`, from: r.id, name: `${r.name}`, q: from.q, r: from.r, strength: 1 + r.level + THREAT_SHAPE.ageStrength[civ.age || 0] }];
            civ = { ...civ, raiders, raiderSeq: seq };
            lines.push(`${r.name} send raiders out from ${placeName(civ, from.q, from.r)}, strength ${1 + r.level}`);
          }
        }
        if (r.age % RIVAL.demandEvery === 0) cards.push({ card: 'rival_demands', rival: r.id });
      } else if (r.stance === 'wary' && way.demandsWhenWary) {
        // The Tall Kings expect gifts, war or no war.
        if (r.age % RIVAL.demandEvery === 0) cards.push({ card: 'rival_demands', rival: r.id });
      } else if (r.stance === 'friendly') {
        if (r.age % way.tradeEvery === 0) {
          let got;
          // An ally's carts come twice as full (the Hall's treaty).
          [civ, got] = applyPatch(civ, r.allied ? Object.fromEntries(Object.entries(way.trade).map(([k, n]) => [k, n * 2])) : way.trade);
          sec = 'income';
          lines.push(`carts come from ${r.name}${r.allied ? ', your allies' : ''}: ${withTotals(civ, got).join(', ')}`);
          sec = 'peoples';
        }
        if (r.age % RIVAL.shareEvery === 0 && r.level > 0) {
          const unknown = PRINCIPLES.flatMap((p) => ASPECTS.filter((a) => loreOf(civ)?.known?.[p]?.[a] === undefined).map((a) => [p, a]));
          if (unknown.length) {
            let ui;
            [ui, rng] = nextInt(unknown.length, rng);
            let said;
            [civ, said] = revealAspect(civ, unknown[ui][0], unknown[ui][1]);
            if (said) lines.push(`the scholars of ${r.name} share a thing: ${said}`);
          }
        }
        if (r.age % (way.tradeEvery * 3) === 0) cards.push({ card: 'rival_offer', rival: r.id });
      }
    }
    // The rival age: a met, unbroken people works at a wonder of its own,
    // and one it finishes first is gone for you (DESIGN.md "THE ARC").
    if (r.met) {
      const work = (r.wonderWork || 0) + 1;
      if (work >= RIVAL_WONDER_EVERY) {
        const open = GOAL_ORDER.filter((id) => GOALS[id]?.build && !wondersOf(civ).includes(id) && !(civ.lostWonders || []).includes(id));
        const stillNeeded = Math.max(0, VICTORY.legacyWonders - wondersOf(civ).length);
        if (open.length > stillNeeded + WONDER_RESERVE) {
          let wi;
          [wi, rng] = nextInt(open.length, rng);
          const took = open[wi];
          civ = { ...civ, lostWonders: [...(civ.lostWonders || []), took], goalOffer: (civ.goalOffer || []).filter((g) => g !== took) };
          lines.push(`${r.name} finish ${GOALS[took].name} - it is theirs now, and gone for you`);
          if (goalOf(civ) === took) {
            civ = { ...civ, goal: null, monument: { revealed: false, stages: 0 }, stoneLaid: false };
            lines.push('the great work you were raising is lost to them');
          }
        }
        r = { ...r, wonderWork: 0, wonders: [...(r.wonders || []), ...(open.length > stillNeeded + WONDER_RESERVE ? [civ.lostWonders.at(-1)] : [])] };
      } else {
        r = { ...r, wonderWork: work };
      }
    }
    civ = { ...civ, rivals: rivalsOf(civ).map((x) => (x.id === r.id ? r : x)) };
  }
  civ = syncThreat(civ);

  // Every raider takes one step toward what it is walking at. A unit of
  // yours standing in the way stops it - that is what garrisoning is.
  sec = 'raids';
  let sacked = false;
  // A people at peace calls its war parties home. They are only SENT while
  // hostile, but one already abroad kept walking after a parley and a
  // treaty, and an ally took a town (playtest 2026-10-20: the Iron Sons,
  // allied that day, took Highfold with a party sent eight months before).
  const atPeace = (x) => {
    const r = x.from && rivalById(civ, x.from);
    return Boolean(r) && (r.stance !== 'hostile' || r.allied);
  };
  const recalled = raidersOf(civ).filter(atPeace);
  if (recalled.length) {
    civ = { ...civ, raiders: raidersOf(civ).filter((x) => !atPeace(x)) };
    [...new Set(recalled.map((x) => x.name))].forEach((name) => lines.push(`${name} call their raiders home`));
  }
  for (const raider of [...raidersOf(civ)]) {
    const live = raidersOf(civ).find((x) => x.id === raider.id);
    if (!live) continue;
    const target = raiderTarget(civ, live);
    if (target && target.d === 0) { /* already there; it strikes below */ } else {
      const step = raiderStep(civ, live);
      if (!step) continue;
      // The Year the River Froze: raiders walk one step further each night.
      // (The second step is taken below only if the first was not blocked.)
      if (blockedBy(civ, step.q, step.r)) {
        lines.push(`${live.name.toLowerCase()} are held at ${placeName(civ, live.q, live.r)}`);
        continue;
      }
      civ = { ...civ, raiders: raidersOf(civ).map((x) => (x.id === live.id ? { ...x, q: step.q, r: step.r } : x)) };
      if (modsOf(civ).raiderStep) {
        const moved = raidersOf(civ).find((x) => x.id === live.id);
        const again = raiderStep(civ, moved);
        if (again && !blockedBy(civ, again.q, again.r)) civ = { ...civ, raiders: raidersOf(civ).map((x) => (x.id === live.id ? { ...x, q: again.q, r: again.r } : x)) };
      }
    }
    const now = raidersOf(civ).find((x) => x.id === live.id);
    const at = raiderTarget(civ, now);
    if (!at || at.d !== 0) continue;

    // Arrived. A raider strikes once and is REMOVED - the camp will send
    // another. Matt: "it's not a strict turn timer, it's based on units
    // being sent from the camp to the village." From the Iron Age a big
    // enough band STAYS: a siege, night after night (THREAT_SHAPE).
    const besieging = (civ.age || 0) >= THREAT_SHAPE.siegeAge && now.strength >= THREAT_SHAPE.siegeAt && !(now.from && rivalById(civ, now.from) && at.kind === 'village');
    if (besieging) {
      const nights = (now.siege || 0) + 1;
      civ = { ...civ, raiders: raidersOf(civ).map((x) => (x.id === now.id ? { ...x, siege: nights } : x)) };
      if (at.kind === 'village') {
        const v = villagesOf(civ).find((x) => x.id === at.id);
        if (!v) continue;
        if (nights >= THREAT_SHAPE.siegeBurnsAfter) {
          civ = dropCharter({ ...civ, villages: villagesOf(civ).filter((x) => x.id !== v.id), raiders: raidersOf(civ).map((x) => (x.id === now.id ? { ...x, siege: 0 } : x)) });
          civ = grieve({ ...civ, grieved: true }, 'mourning');
          lines.push(`the siege of ${v.name} ends: ${now.name.toLowerCase()} burn it to the ground`, 'the Hall takes up the mourning');
        } else {
          civ = { ...civ, villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, damaged: true } : x)) };
          lines.push(`${now.name.toLowerCase()} lay siege to ${v.name} (night ${nights} of ${THREAT_SHAPE.siegeBurnsAfter}) - break it with a warband`);
        }
        continue;
      }
      // At the city the siege falls through to the burning below, every night.
      lines.push(`${now.name.toLowerCase()} besiege the city (night ${nights})`);
    }
    if (!besieging) civ = { ...civ, raiders: raidersOf(civ).filter((x) => x.id !== now.id) };
    civ = pushCompass(civ, { y: -1 });
    if (at.kind === 'village' && now.from && rivalById(civ, now.from)) {
      // Another people's war party: a VILLAGE is razed, a TOWN is taken
      // (Matt, 2026-09-28: "villages should get razed if they get conquered
      // and building them into a town is what makes them reclaimable"). A
      // taken town stands under their colour and sends nothing until a
      // warband walks into it.
      const v = villagesOf(civ).find((x) => x.id === at.id);
      if (!v) continue;
      if (!v.chartered) {
        civ = dropCharter({ ...civ, villages: (civ.villages || []).filter((x) => x.id !== v.id) });
        civ = grieve({ ...civ, grieved: true }, 'mourning');
        lines.push(`${now.name} raze ${v.name} to the ground`, 'the Hall takes up the mourning');
        continue;
      }
      civ = { ...civ, villages: (civ.villages || []).map((x) => (x.id === v.id ? { ...x, taken: now.from, damaged: false } : x)) };
      civ = { ...civ, rivals: rivalsOf(civ).map((r) => (r.id === now.from ? { ...r, held: [...new Set([...(r.held || []), `${v.q},${v.r}`])] } : r)) };
      lines.push(`${now.name} take ${v.name} - theirs until a warband takes it back`);
      continue;
    }
    if (at.kind === 'village') {
      const v = villagesOf(civ).find((x) => x.id === at.id);
      if (!v) continue;
      let lastFaces = [];
      // Palisades and Masonry: the village or town throws its own dice
      // first, and a hit turns the raiders back at the fence.
      const fence = Math.max(0, modsOf(civ).villageDice) + (v.chartered ? Math.max(0, modsOf(civ).townDice) : 0)
        + (v.city ? CITY.dice + (v.focus === 'defence' ? 2 : 0) : 0);
      if (fence) {
        const faces = [];
        lastFaces = faces;
        let hits = 0;
        for (let i = 0; i < fence; i += 1) { let f; [f, rng] = nextInt(6, rng); faces.push(f + 1); if (f + 1 >= ourHitOn(civ)) hits += 1; }
        if (hits) { lines.push(`${now.name.toLowerCase()} reach ${v.name} and the fence throws ${faces.join(' + ')}: turned back`); continue; }
      }
      // One line, in the order it happened (playtest 12: "Ashby did kill
      // them but they made it into the city and then they wrecked it" - the
      // fence's miss and the sack were two lines, and the raiders just
      // vanished). A raid is spent once it lands.
      const fenceSaid = fence ? `get past ${v.name}'s fence (it throws ${lastFaces.join(' + ')} and misses) and ` : '';
      const raids = (v.raids || 0) + 1;
      if (raids >= VILLAGE.raidsToBurn) {
        civ = { ...civ, villages: villagesOf(civ).filter((x) => x.id !== v.id) };
        // Its Charter turns into a grievance: one round of feeling bad, a
        // happiness a night if it is left in hand, and then out of the deck
        // for good. Exactly what the Hall's grievance rule already does.
        // Its Charter is drawn back out and a mourning goes in its place.
        // Matt, 2026-09-20: "there's like one turn of 'damn, that village
        // got burned down and we feel bad about it', and then the card is
        // out of the deck." That is exactly what a grievance already does.
        civ = dropCharter(civ);
        civ = grieve({ ...civ, grieved: true }, 'mourning');
        lines.push(`${now.name.toLowerCase()} ${fenceSaid}burn ${v.name} to the ground`, 'the Hall takes up the mourning');
      } else {
        civ = {
          ...civ,
          villages: villagesOf(civ).map((x) => (x.id === v.id ? { ...x, damaged: true, raids } : x)),
        };
        lines.push(`${now.name.toLowerCase()} ${fenceSaid}sack ${v.name} (${raids} of ${VILLAGE.raidsToBurn}), and scatter`);
      }
      continue;
    }

    // At the city: one building burns. THE CITY IS SACKED when a raider
    // arrives and there is nothing left to burn - which scales itself, and
    // makes building the city its own defence.
    const city = cityOf(civ);
    const standing = city.buildings.filter((b) => !b.burned);
    // The wall, the watch, the steel: the city throws its garrison dice
    // first, and a hit turns the raiders back before they burn anything -
    // or sack a town with nothing left standing (the dice used to come
    // after that test, so the Watch could never save a burnt-out city).
    const garrison = GARRISON_BASE + Math.max(0, modsOf(civ).garrisonDice);
    if (garrison) {
      const faces = [];
      let hits = 0;
      for (let i = 0; i < garrison; i += 1) { let f; [f, rng] = nextInt(6, rng); faces.push(f + 1); if (f + 1 >= ourHitOn(civ)) hits += 1; }
      if (hits) { lines.push(`${now.name.toLowerCase()} reach the city and the walls throw ${faces.join(' + ')}: turned back`); continue; }
      lines.push(`the walls throw ${faces.join(' + ')} and miss`);
    }
    if (!standing.length) {
      sacked = true;
      lines.push(`${now.name.toLowerCase()} reach the city and there is nothing left standing`);
      continue;
    }
    const edge = edgeHexToward(civ, now.q, now.r);
    const victim = standing.find((b) => edge && b.hex[0] === edge[0] && b.hex[1] === edge[1]) || standing[0];
    civ = syncCity({
      ...civ,
      city: { ...city, buildings: city.buildings.map((b) => (b.id === victim.id ? { ...b, burned: true } : b)) },
    });
    lines.push(`${now.name.toLowerCase()} reach the city: the ${BUILDINGS[victim.type].name.toLowerCase()} burns`);
  }
  civ = syncThreat(civ);
  if (sacked) {
    const done = logLine({ ...state, civ, rng }, { kind: 'end', text: lines.join('. ') });
    return finish(done, { won: false, reason: LOSS.SACKED });
  }

  // The stores: what the granary and warehouse cannot hold spoils.
  // The Harvest's overflow keeps one night, so the table has a day to spend it.
  sec = 'stores';
  const heaped = civ.harvestNight === state.meta.round;
  const spoilFood = heaped ? 0 : Math.max(0, civ.food - foodCap(civ));
  const spoilGoods = heaped ? 0 : Math.max(0, civ.goods - goodsCap(civ));
  if (heaped && civ.food > foodCap(civ)) lines.push(`the granary is heaped past its ${foodCap(civ)}: what is over it spoils tomorrow night`);
  if (spoilFood || spoilGoods) {
    civ = { ...civ, food: civ.food - spoilFood, goods: civ.goods - spoilGoods };
    lines.push(`the stores overflow: ${[spoilFood ? `${spoilFood} food spoils (${foodCap(civ)} keeps)` : '', spoilGoods ? `${spoilGoods} goods rot (${goodsCap(civ)} keep)` : ''].filter(Boolean).join(', ')}`);
  }
  // Hands leave the buildings for the night.
  civ = { ...civ, city: { ...cityOf(civ), buildings: cityOf(civ).buildings.map((b) => ({ ...b, staffed: 0 })) } };
  // The Hall's night: grievances left in hand fester, the hand is
  // discarded, the extremes of the compass cost. The next hand is dealt
  // when the day starts, by the workers the dawn split gives the Hall.
  sec = 'mood';
  if (hallOf(civ)) {
    const hall = hallOf(civ);
    const festering = hall.hand.filter((id) => HALL_CARDS[id]?.grievance).length;
    if (festering) {
      let fest;
      [civ, fest] = applyPatch(civ, { happiness: -festering });
      lines.push(`${festering} ${festering === 1 ? 'grievance festers' : 'grievances fester'}: ${withTotals(civ, fest).join(', ')}`);
    }
    let h = { ...hallOf(civ), hand: [], discard: [...hallOf(civ).discard, ...hallOf(civ).hand], used: {}, played: [], buys: HALL_BUYS_PER_DAY };
    if (Math.abs(h.compass.y) >= COMPASS_MAX) {
      let chafe;
      [civ, chafe] = applyPatch(civ, { happiness: -1 });
      lines.push(`the compass is at full ${h.compass.y < 0 ? 'Order' : 'Liberty'}: ${withTotals(civ, chafe).join(', ')} every night until it moves back a step`);
    }
    if (h.compass.x >= COMPASS_MAX) {
      const at = h.deck.findIndex((id) => id === 'old_custom') >= 0 ? h.deck.findIndex((id) => id === 'old_custom') : h.discard.findIndex((id) => id === 'old_custom');
      if (at >= 0) {
        if (h.deck.includes('old_custom')) h = { ...h, deck: h.deck.filter((id, i) => i !== h.deck.indexOf('old_custom')) };
        else h = { ...h, discard: h.discard.filter((id, i) => i !== h.discard.indexOf('old_custom')) };
        sec = 'hall';
        lines.push('the compass is at full Novelty: an Old Custom leaves the deck');
        sec = 'mood';
      }
    }
    let rng3 = rng;
    [h, rng3] = refillMarket(h, { ...civ, hall: h }, rng3);
    rng = rng3;
    civ = { ...civ, hall: h };
    // Only when it CHANGES (playtest 12: said every night, read by nobody).
    sec = 'hall';
    const gov = governmentOf(civ);
    const was = governmentOf(state.civ);
    if (gov !== was) lines.push(gov ? `the Hall now sits as ${GOVERNMENTS[gov].name}: ${GOVERNMENTS[gov].short}` : `the Hall is no longer ${GOVERNMENTS[was].name}`);
  }
  // The Scholars' night: every theory published today is held up or falls.
  // A save from before the machines is dealt their laws.
  let boons = { ...emptyByRole(0), ...(state.boons || {}) };
  if (loreOf(civ)) {
    let rngL = rng;
    [civ, rngL] = dealMissingMachines(civ, rngL);
    rng = rngL;
  }
  sec = 'income';
  // THE NETWORK SENDS UP WHAT IT MADE, before dawn. Playtest 11 moved it
  // from the night to the start of the day ("so then we already have it");
  // playtest 13 moved it one step earlier still, to the end of the night,
  // because the DAWN SPLIT is where it is needed (Matt: "every time I have
  // to say out loud that we're gonna get more from the village - it should
  // already be accounted for when we start the day"). After spoilage, so
  // what arrives tonight is in the stores at dawn whatever the caps say.
  {
    const brought = {};
    villagesOf(civ).forEach((v) => {
      Object.entries(villageYield(civ, v)).forEach(([k, n]) => { brought[k] = (brought[k] || 0) + n; });
    });
    if (Object.keys(brought).length) {
      let got;
      [civ, got] = applyPatch(civ, brought);
      const n = villagesOf(civ).filter((v) => !v.damaged).length;
      lines.push(`${n} ${n === 1 ? 'settlement sends' : 'settlements send'} up ${withTotals(civ, got).join(', ')} - in the stores for the dawn`);
    }
  }
  if (loreOf(civ)) {
    const lore = loreOf(civ);
    let settled = { ...lore.settled };
    let debunked = [...(lore.debunked || [])];
    for (const [p, theory] of Object.entries(lore.published || {})) {
      const truth = lore.natures[p];
      if (sameNature(theory, truth)) {
        settled = { ...settled, [p]: { ...truth } };
        let paid;
        [civ, paid] = applyPatch(civ, THEORY_HOLDS);
        lines.push(`the theory holds: ${PRINCIPLE_NAMES[p]} is ${describeNature(truth)} - settled knowledge, ${withTotals(civ, paid).join(', ')}`);
        if (theory.teach && ROLES.includes(theory.teach)) {
          boons = { ...boons, [theory.teach]: (boons[theory.teach] || 0) + 1 };
          // The delay is the theme: the town sleeps on it, and someone who
          // heard it turns out at dawn to help the role the Scholars named.
          lines.push(`a student turns out at dawn for the ${ROLE_INFO[theory.teach].label}`);
        }
      } else {
        debunked = [...debunked, { principle: p, nature: theory, round: state.meta.round }];
        let lost;
        [civ, lost] = applyPatch(civ, THEORY_DEBUNKED);
        lines.push(`the theory falls: ${PRINCIPLE_NAMES[p]} is not ${describeNature(theory)} - debunked, ${withTotals(civ, lost).join(', ')}`);
      }
    }
    civ = patchLore(civ, { settled, debunked, published: {} });
  }
  sec = 'hall';
  if (hallOf(civ)) {
    // The council wears out on a timer: whoever's term is up tonight
    // retires, and an empty chair gets an OFFER of two for the Hall to
    // choose from at dawn (Matt, playtest 6). A save from before terms is
    // dealt them here.
    let rngC = rng;
    [civ, rngC] = dealMissingTerms(civ, rngC);
    // A save from before Paths and Highways gets their reactions tonight.
    [civ, rngC] = dealMissingReactions(civ, TECHS, rngC);
    // Whose moment came today (playtest 14, the Scholars: "I was told the
    // Scholar could sit - what happened?"): say so, and what it takes.
    const couldBefore = emerging(state.civ, state.meta.round + 1);
    emerging(civ, state.meta.round + 1).filter((id) => !couldBefore.includes(id)).forEach((id) => {
      lines.push(`${COUNSELORS[id].name}, ${COUNSELORS[id].job}, could sit on the council now (${COUNSELORS[id].likes?.text || ''}) - a chair must empty first`);
    });
    retiring(civ, state.meta.round + 1).forEach((c) => {
      civ = retire(civ, c.id, state.meta.round + 1);
      // A counselor who served long enough is remembered: a keepsake card
      // in the Hall's deck, in their name (DESIGN.md "The Year").
      const served = state.meta.round + 1 - c.since;
      if (served >= KEEPSAKE_AFTER && keepsakeOf(c.id)) {
        civ = remember(civ, c.id);
        lines.push(`${COUNSELORS[c.id]?.name || 'a counselor'} retires from the Hall after ${served} months, and leaves ${HALL_CARDS[keepsakeOf(c.id)].name} in the deck`);
      } else lines.push(`${COUNSELORS[c.id]?.name || 'a counselor'} retires from the Hall after ${served} months`);
    });
    // An offer that has gone stale (its people seated elsewhere) is redrawn.
    const stale = offerOf(civ).filter((id) => civ.hall.council.some((x) => x.id === id));
    if (stale.length) civ = { ...civ, hall: { ...civ.hall, offer: [] } };
    if (civ.hall.council.length < COUNCIL_SEATS && !offerOf(civ).length) {
      let offer;
      [offer, rngC] = drawOffer(civ, state.meta.round + 1, rngC);
      if (offer.length) {
        civ = { ...civ, hall: { ...civ.hall, offer } };
        const empty = COUNCIL_SEATS - civ.hall.council.length;
        lines.push(`${empty > 1 ? `${empty} chairs are empty, filled one at a time - for the first` : 'a chair is empty'}: ${offer.map((id) => COUNSELORS[id].name).join(' or ')} could take it - the Hall chooses`);
      } else {
        // Nobody emerged: a moot (playtest 7 - the council ran dry).
        let moot;
        [moot, rngC] = drawMoot(civ, rngC);
        if (moot.length) {
          civ = { ...civ, hall: { ...civ.hall, offer: moot, moot: true } };
          lines.push(`nobody has come forward, so the square holds a moot: ${moot.map((id) => COUNSELORS[id].name).join(' or ')} - the Hall backs one, the die decides`);
        } else {
          lines.push('a chair in the Hall stands empty, and nobody has come forward');
        }
      }
    }
    // THE SHELF FOLLOWS THE COUNCIL (playtest 13): whoever left takes their
    // idea down, whoever sits puts one up, and one counselor a night swaps
    // theirs for another of their own. The burden slot is filled if empty.
    let shelf;
    [shelf, rngC] = refillMarket(civ.hall, civ, rngC);
    let swapped;
    [shelf, rngC, swapped] = rotateShelf(shelf, { ...civ, hall: shelf }, state.meta.round, rngC);
    if (swapped) lines.push(swapped);
    [shelf, rngC] = dealBurden(shelf, rngC);
    civ = { ...civ, hall: shelf };
    rng = rngC;
  }
  sec = 'raids';
  const loose = raidersOf(civ).length;
  if (loose) lines.push(`${loose} ${loose === 1 ? 'band of raiders is' : 'bands of raiders are'} abroad`);
  state = { ...state, rng };
  // What the neighbours want to say lands in the deck near the top.
  sec = 'peoples';
  for (const c of cards) {
    civ = { ...civ, rivalMeeting: c.rival, rivalCards: [...(civ.rivalCards || []), { card: c.card, rival: c.rival }] };
    state = seedNext({ ...state, civ }, c.card);
    civ = state.civ;
    lines.push(`${EVENTS[c.card].name.toLowerCase()} from ${rivalById(civ, c.rival)?.name || 'a neighbouring people'} come next month`);
  }
  // The new round's workers turn out on their own - fewer if the people
  // are unhappy. This is why nobody has to spend a turn calling them.
  // (Said at dawn, not at night: playtest 12.)
  sec = 'skip';
  const extra = civ.extraWorkers || 0;
  const glad = duskHappiness >= GLAD_WORKER_AT && civ.happiness < GLAD_WORKER_AT ? 1 : 0;
  const due = workersDue(civ) + glad;
  civ = { ...civ, workers: due, extraWorkers: 0 };
  lines.push(due === 0 ? 'nobody turns out to work' : `${due} workers turn out for the new round${extra ? ` (${extra} of them the family, who move on after)` : ''}${duskHappiness >= GLAD_WORKER_AT ? ' (one more: happiness 9 or more)' : ''}`);

  let firstSplit;
  [firstSplit, rng] = defaultSplit(civ.workers, state.allocation, rng);
  let next = {
    ...state,
    civ,
    rng,
    event: null,
    allocation: firstSplit,
    hands: emptyByRole(0),
    done: emptyByRole(false),
    taken: emptyByRole(null),
    boons,
    meta: { ...state.meta, phase: PHASES.DAWN, round: state.meta.round + 1 },
  };
  // What the town has become can open a branch for Science.
  // A NEW AGE (cards.js AGES): crossed tonight, its card turns next month.
  sec = 'people';
  if (ageOf(civ) > (civ.age || 0)) {
    const age = ageOf(civ);
    next = { ...next, civ: { ...next.civ, age } };
    next = seedNext(next, `age_${AGES[age].id}`);
    lines.push(`the town enters ${AGES[age].name}`);
  }
  if (civ.happiness >= 8) { let o; [next, o] = openBranch(next, 'arts', 'a town with time to sing'); if (o) lines.push(o); }
  if (civ.population >= 7) { let o; [next, o] = openBranch(next, 'statecraft', 'too many people to govern by shouting'); if (o) lines.push(o); }
  // Prosperity's streak (the victories are on the table from year 1).
  next = { ...next, civ: { ...next.civ, prosperNights: nextProsperNights(next.civ) } };
  // What the Frontier's phone stops for at dawn: everything the night changed on the map.
  next = logLine(next, {
    kind: 'upkeep',
    text: `End of round ${state.meta.round}: ${lines.join(', ')}.`,
    notices: frontierNotices(state.civ, civ),
    ...revealsEntry(state.civ, civ, 'the night'),
    night: nightCards(beats, state.meta.round),
  });
  // Nobody has read tonight yet.
  next = { ...next, nightRead: { round: state.meta.round, at: 0 } };
  next = checkGoal(next, { atNight: true });
  if (next.meta.phase === PHASES.FINISHED) return bump(next);
  next = checkCollapse(next);
  if (next.meta.phase === PHASES.FINISHED) return bump(next);
  // DECEMBER'S NIGHT ENDS THE YEAR AND THE SITTING. The chronicle goes on
  // the table and nothing continues until somebody taps Done; the next
  // sitting opens on the same page, then January's dawn.
  if (isYearEnd(state.meta.round)) {
    const page = writeChronicle(next, next.civ, state.meta.round);
    next = {
      ...next,
      chronicle: [...(next.chronicle || []), page],
      yearEnd: page,
      meta: { ...next.meta, phase: YEAR_END, year: page.year },
    };
    next = logLine(next, { kind: 'year', text: `Year ${page.year} is over: ${page.lines.join('; ')}.` });
  }
  return bump(next);
};

/** The new year: the deck is dealt, the tales that start a year land, and January dawns. */
const applyNewYear = (state) => {
  if (state.meta.phase !== YEAR_END) return state;
  let next = dealYear(state);
  let civ = next.civ;
  const lines = [];
  if (modsOf(civ).yearJoy) {
    let joy;
    [civ, joy] = applyPatch(civ, { happiness: modsOf(civ).yearJoy });
    if (joy.length) lines.push(`the Quiet Year: ${withTotals(civ, joy).join(', ')}`);
  }
  if (modsOf(civ).springReveal && loreOf(civ)) {
    const unknown = PRINCIPLES.flatMap((p) => ASPECTS.filter((a) => loreOf(civ)?.known?.[p]?.[a] === undefined).map((a) => [p, a]));
    if (unknown.length) {
      let ui;
      let { rng } = next;
      [ui, rng] = nextInt(unknown.length, rng);
      let said;
      [civ, said] = revealAspect(civ, unknown[ui][0], unknown[ui][1]);
      next = { ...next, rng };
      if (said) lines.push(`by the comet's light the Scholars read: ${said}`);
    }
  }
  civ = { ...civ, yearStart: yearSnapshot(civ) };
  next = { ...next, civ, yearEnd: null, meta: { ...next.meta, phase: PHASES.DAWN } };
  next = logLine(next, { kind: 'order', text: `${dateOf(next.meta.round)}: a new year. ${lines.length ? `${lines.join(', ')}. ` : ''}The deck holds ${next.deck.length} cards.` });
  return bump(next);
};

// ------------------------------------------------------------ deciding together
// Matt, playtest 9: "let's say that everybody has to say flip the card and
// when all four of us said it gets flipped, and then everybody has to pick
// a thing... if three people vote on one option and one person votes on the
// other option it should then do a dramatic little animation while it
// chooses and it should be 75% chance of picking one and 25% of the other."
// Seth: "the fact that all four of us have flip the card as an option... it
// makes it feel like the thing I'm doing is not affecting you all."

// BOTS (Matt, 2026-10-12: "a play test mode where I could do it solo and play
// with some bots... just focus on one player's experience"). A bot holds a
// role like anyone else, but it is not at the table: it never says flip and
// never votes, so nothing waits on it.
export const BOT_PREFIX = 'bot:';
export const isBot = (id) => typeof id === 'string' && id.startsWith(BOT_PREFIX);
export const botFor = (role) => `${BOT_PREFIX}${role}`;
const seatedIds = (state) => (state.players || []).filter((p) => !p.bot && !isBot(p.id)).map((p) => p.id);

/** Who has not yet said flip, or voted. */
export const waitingOn = (state) => {
  const ids = seatedIds(state);
  if (state.meta?.phase === PHASES.DAWN && state.startVotes) return ids.filter((id) => !state.startVotes[id]);
  if (state.meta?.phase === PHASES.TALLY) return ids.filter((id) => !state.flipVotes?.[id]);
  if (state.meta?.phase === PHASES.EVENT && state.event && !state.event.resolved) {
    return ids.filter((id) => state.event.votes?.[id] == null);
  }
  return [];
};

const applyVoteFlip = (state, { by, force }) => {
  if (state.meta.phase !== PHASES.TALLY || !seatedIds(state).includes(by)) return state;
  const flipVotes = { ...(state.flipVotes || {}), [by]: true };
  const everyone = seatedIds(state).every((id) => flipVotes[id]);
  if (!everyone && !force) return bump({ ...state, flipVotes });
  return bump(flipEvent({ ...state, flipVotes: null }));
};

const applyVote = (state, { by, choice, force }) => {
  if (state.meta.phase !== PHASES.EVENT || !state.event || state.event.resolved) return state;
  const ids = seatedIds(state);
  if (!ids.includes(by)) return state;
  const options = state.event.choices || EVENTS[state.event.cardId]?.choices || [];
  const votes = { ...(state.event.votes || {}) };
  if (choice != null) {
    const option = options.find((c) => c.id === choice);
    if (!option || (option.requires && !canAfford(state.civ, option.requires))) return state;
    votes[by] = choice;
  }
  const cast = ids.filter((id) => votes[id] != null);
  if (!cast.length) return state;
  if (cast.length < ids.length && !force) return bump({ ...state, event: { ...state.event, votes } });
  // One ballot drawn at random, so the odds are the heads: 3-1 is 75/25.
  const ballots = cast.map((id) => votes[id]);
  const tally = {};
  ballots.forEach((c) => { tally[c] = (tally[c] || 0) + 1; });
  const split = Object.keys(tally).length > 1;
  let { rng } = state;
  let picked = ballots[0];
  if (split) {
    let at;
    [at, rng] = nextInt(ballots.length, rng);
    picked = ballots[at];
  }
  const resolved = applyResolveEvent({ ...state, rng, event: { ...state.event, votes } }, { choice: picked });
  if (!resolved.event?.resolved) return state;
  return { ...resolved, event: { ...resolved.event, vote: { tally, picked, split } } };
};

const applyNeed = (state, { role, need }) => {
  if (!ROLES.includes(role) || (need !== null && !NEEDS.includes(need))) return state;
  if ((state.needs?.[role] ?? null) === need) return state;
  return bump({ ...state, needs: { ...(state.needs || {}), [role]: need } });
};

const applyRename = (state, { by, village, name }) => {
  if (!seatedIds(state).includes(by)) return state;
  const clean = String(name || '').replace(/\s+/g, ' ').trim().slice(0, VILLAGE_NAME_MAX);
  const v = villagesOf(state.civ).find((x) => x.id === village);
  if (!clean || !v || v.name === clean) return state;
  const civ = { ...state.civ, villages: villagesOf(state.civ).map((x) => (x.id === village ? { ...x, name: clean } : x)) };
  return bump(logLine({ ...state, civ }, { kind: 'order', text: `${v.name} is renamed ${clean}.` }));
};

// Villages founded before they had names were called after their ground in
// lower case ("the mountains"); say them like names.
const capitalNames = (state) => {
  const vs = state?.civ?.villages;
  if (!vs || !vs.some((v) => /^[a-z]/.test(v.name || ''))) return state;
  return { ...state, civ: { ...state.civ, villages: vs.map((v) => ({ ...v, name: (v.name || '').replace(/^[a-z]/, (c) => c.toUpperCase()) })) } };
};

export const dispatch = (state, action) => {
  if (!state || !action) return state;
  const next = step(state, action);
  return next === state ? state : remembered(capitalNames(next), state);
};

// Every unit lost, however it fell, puts a We Remember into the deck.
const remembered = (after, before) => {
  const lost = unitsOf(before.civ).filter((u) => u.kind !== 'pioneer' && !unitsOf(after.civ).some((v) => v.id === u.id));
  if (!lost.length || !EVENTS.fallen) return after;
  return lost.reduce((s) => seedCard(s, 'fallen'), after);
};

const step = (state, action) => {
  switch (action.type) {
    case ACTIONS.ALLOCATE: return applyAllocate(state, action);
    case ACTIONS.START_DAY: return applyStartDay(state, action);
    case ACTIONS.ACT: return applyAct(state, action);
    case ACTIONS.UNDO: return applyUndo(state, action);
    case ACTIONS.DONE: return applyDone(state, action);
    case ACTIONS.UNDONE: return applyUndone(state, action);
    case ACTIONS.RESOLVE_EVENT: return applyResolveEvent(state, action);
    case ACTIONS.CONTINUE: return applyContinue(state);
    case ACTIONS.VOTE_FLIP: return applyVoteFlip(state, action);
    case ACTIONS.VOTE: return applyVote(state, action);
    case ACTIONS.RENAME: return applyRename(state, action);
    case ACTIONS.NEED: return applyNeed(state, action);
    case ACTIONS.NIGHT_NEXT: return applyNightNext(state, action);
    default: return state;
  }
};

// ------------------------------------------------------------ queries

/** May this role act right now: it is the day and it hasn't finished. */
export const roleMayAct = (state, role) => state?.meta.phase === PHASES.DAY && !state.done?.[role];

/** The day's work so far, by role: the act lines of this round. */
export const dayLog = (state) => (state?.log || []).filter((l) => l.kind === 'act' && l.round === state.meta.round);

export const playerForRole = (state, role) => state?.roles?.[role] ?? null;

export const rolesOf = (state, playerId) => ROLES.filter((r) => state?.roles?.[r] === playerId);

/** What the night eats: the people, and a town's upkeep each. */
export const foodEatenPerRound = (civ) => Math.ceil(civ.population * FOOD_PER_PERSON)
  + villagesOf(civ).filter((v) => v.chartered && !v.city).length * TOWN.upkeepFood
  + villagesOf(civ).filter((v) => v.city).length * CITY.upkeepFood;

export const allocationOf = (state) => ({ ...emptyByRole(0), ...(state?.allocation || {}) });
export const handsOf = (state) => ({ ...emptyByRole(0), ...(state?.hands || {}) });
